import type { Translations } from "../index";

export const ru: Translations = {
  lang: {
    en: "EN",
    ja: "日本語",
    "zh-HK": "繁中",
    "zh-TW": "繁中",
    "zh-CN": "简中",
    ko: "한국어",
    th: "ไทย",
    ru: "Русский",
    current: "Русский",
  },

  // ─── Global nav ──────────────────────────────────────────────────────────
  nav: {
    platform: "Платформа",
    forDesigners: "Дизайнерам",
    forBrands: "Брендам",
    forManufacturers: "Производство",
    forStudio: "Студия",
    caraAi: "Cara AI",
    pricing: "Тарифы",
    more: "Ещё",
    about: "О нас",
    academy: "Академия",
    accessPlatform: "Войти в платформу →",
    subline: "Где дизайн встречает производство",
    sublineBrand: "Найти · Произвести · Запустить",
    sublineDesigner: "Ваш подиум для ювелирных украшений",
    inspire: "Inspire",
    newsroom: "Пресс-центр",
    contact: "Контакты",
    catalog: "Каталог",
    showcase: "Витрина",
    switchToDesigners: "Дизайнерам →",
    switchToBrands: "Брендам →",
    howItWorks: "Как это работает",
    overview: "Обзор",
  },

  // ─── Footer ──────────────────────────────────────────────────────────────
  footer: {
    subline: "Дизайнерские украшения · B2B-закупки · Производство EPH",
    tagline: "«Маркетплейс, где исключительный дизайн\nвстречает безупречное производство.»",
    accessPlatform: "Войти в платформу →",
    ephCertified: "Сертифицированное производство EPH",
    copyright: "Caratzon Ltd. Все права защищены.",
    columns: {
      platform: "Платформа",
      company: "Компания",
      resources: "Ресурсы",
      contact: "Контакты",
    },
    links: {
      forDesigners: "Дизайнерам",
      forBrands: "Брендам",
      forManufacturers: "Производство",
      caraAi: "Cara AI",
      tradeCatalogue: "Торговый каталог",
      accessPlatform: "Войти в платформу",
      aboutCaratzon: "О Caratzon",
      ourStory: "Наша история",
      leadership: "Руководство",
      pressMedia: "Пресса и СМИ",
      investorRelations: "Для инвесторов",
      howItWorks: "Как это работает",
      ephHub: "Производственный хаб EPH",
      sustainabilityReport: "Отчёт об устойчивом развитии",
      cadStandards: "Стандарты CAD",
      supplierCode: "Кодекс поставщика",
      academy: "Академия",
      inspire: "Inspire",
      newsroom: "Пресс-центр",
      aiCredits: "AI-кредиты",
      contactUs: "Связаться с нами",
      becomeManufacturer: "Стать производственным партнёром",
    },
    offices: {
      hongkong: "Design Gateway · 香港",
      guangdong: "Manufacturing Hub · 广东",
      london: "Европейский торговый офис",
    },
    legal: {
      terms: "Условия использования",
      privacy: "Политика конфиденциальности",
      cookies: "Политика Cookie",
      supplierCode: "Кодекс поставщика",
    },
  },

  // ─── Home page ───────────────────────────────────────────────────────────
  home: {
    meta: {
      title: "Caratzon — B2B-закупки ювелирных украшений · Инженерные CAD-дизайны · AI-маркетинг",
      description:
        "Ведущая B2B-платформа для закупок авторских ювелирных украшений. Лицензируйте готовые к производству CAD-дизайны, производите устойчиво в EPH и мгновенно создавайте маркетинговые материалы с Caratzon Studio. Три уровня IP-прав, производство под ключ.",
    },
    emergence: {
      tagline: "От эскиза до витрины. Дизайн, лицензия, производство — только Caratzon.",
      subtitle: "B2B-платформа для дизайна, лицензирования и производства ювелирных изделий",
      cta: "Смотреть каталог",
      ctaSecondary: "Как это работает",
    },
    process: {
      overline: "Как это работает",
      steps: [
        { number: "01", heading: "Откройте", body: "Просмотрите готовые оригинальные дизайны от независимых мастеров со всего мира.", hook: "Без затрат на R&D. Без NRE." },
        { number: "02", heading: "Лицензируйте", body: "Выберите уровень IP и получите коммерческие права за секунды.", hook: "Маркетинговые материалы включены в каждую лицензию." },
        { number: "03", heading: "Производите", body: "Дизайны направляются на устойчивое производство EPH. Готово.", hook: "Ноль управления производством. Полная прослеживаемость." },
      ],
    },
    storyBeat: {
      line1: "От эскиза до витрины.",
      line2: "Дизайн, лицензирование, производство.",
      line3: "Только Caratzon.",
      splitLabel: "Я",
      splitDesigner: "Дизайнер",
      splitBrand: "Бренд-байер",
    },
    showcase: {
      pieces: [
        {
          sku: "FD001-251225032",
          designer: "Студия Я Юй",
          name: "Ожерелье «Небесная драпировка»",
          descriptor: "Жидкое золото, парящее в свете.",
        },
        {
          sku: "FD016-251224017",
          designer: "Золотой Хулу",
          name: "Подвеска «Тыква удачи»",
          descriptor: "Древний символ, современный силуэт.",
        },
        {
          sku: "FD033-251225049",
          designer: "Ателье Пион",
          name: "Серьги «Падающий цветок»",
          descriptor: "Лепестки, застывшие в падении.",
        },
        {
          sku: "FD051-251226013",
          designer: "Нефритовая Река",
          name: "Браслет «Рябь»",
          descriptor: "Вода на запястье, застывшая в золоте.",
        },
      ],
    },
    stats: [
      { value: "48+", numericValue: 48, label: "Оригинальных дизайнов" },
      { value: "12", numericValue: 12, label: "Независимых дизайнеров" },
      { value: "6", numericValue: 6, label: "Стран" },
      { value: "80%", numericValue: 80, label: "Доля дохода дизайнера", suffix: "%" },
    ],
    fork: {
      heading: "Итак, к какому миру вы принадлежите?",
      designer: {
        eyebrow: "Для дизайнеров",
        line: "Ваше мастерство заслуживает мировой сцены — а не очередного поста в Instagram.",
        cta: "Войти как дизайнер",
      },
      brand: {
        eyebrow: "Для брендов",
        line: "Без R&D. Лицензия с маркетинговыми материалами. Запуск сегодня.",
        cta: "Войти как бренд",
      },
    },
    close: {
      line: "Следующая глава начинается здесь.",
      cta: "Начать",
    },
    hero: {
      overline: "B2B-платформа закупок · Устойчивое производство через EPH",
      h1Line1: "Возьмите готовый дизайн.",
      h1Line2: "Забудьте об R&D.",
      h1Line3: "Запустите мгновенно.",
      tagline:
        "Ведущая B2B-платформа для закупок авторских ювелирных украшений. Лицензируйте готовые к производству CAD-дизайны, производите устойчиво в нашем хабе EPH, а наша AI Studio сгенерирует маркетинговые материалы в момент, когда вы нажмёте «купить».",
      cta: "Открыть торговый каталог",
      ctaSecondary: "Узнать о Caratzon Studio",
    },
    pillars: {
      overline: "Возможности платформы",
      subtitle: "Полный производственный цикл",
      items: [
        {
          heading: "AI-маркетинговый движок (Caratzon Studio)",
          body: "Не ждите недели, пока образцы отснимут. Каждая лицензия включает Essentials Kit (снимки для e-commerce, характеристики и SEO-тексты). Обновитесь до Caratzon Kit — и получите лайфстайл-фотографии, контент для соцсетей и PDF-прайс-листы в стиле вашего бренда.",
        },
        {
          heading: "Три уровня IP-прав с юридическим оформлением",
          body: "Стандартная лицензия (неисключительная), Сезонное эмбарго (12-месячная эксклюзивность) или Полная передача IP (бессрочная). Юридически обязывающие коммерческие права — не просто рукопожатие.",
        },
        {
          heading: "Производство EPH под ключ",
          body: "Ваш заказ поступает напрямую в проверенную производственную сеть EPH. Литьё, закрепка камней, контроль качества и логистика — под одной крышей, в едином устойчивом хабе. Никаких переговоров с заводами.",
        },
      ],
    },
    quote: {
      text: "«Ваша следующая коллекция — без R&D, без агентств, без головной боли с заводами.\nОт закупки до полки — одна платформа.»",
      attribution: "Философия Caratzon",
    },
    marquee: {
      ariaLabel: "Предпросмотр каталога",
    },
    howItWorks: {
      overline: "Как это работает",
      subtitle: "Три шага",
      items: [
        {
          heading: "Откройте для себя",
          body: "Просматривайте готовые к производству оригиналы от независимых авторов. Для каждого изделия доступен Essentials Kit — каталожные снимки, характеристики, редакционная история и интерактивный 3D-просмотр, чтобы оценить полный пакет до принятия решения.",
        },
        {
          heading: "Лицензируйте и генерируйте",
          body: "Выберите уровень IP-прав (Стандартный, Эмбарго или Vault) и получите коммерческие права за считанные секунды. Как только лицензия оформлена, Caratzon Studio генерирует полный маркетинговый пакет — ваша команда продаж может начинать работу немедленно.",
        },
        {
          heading: "Произведите через EPH",
          body: "Утверждённые дизайны поступают на консолидированный завод EPH. Устойчивая гальваника, сертифицированные экопроцессы, пооперационный контроль — пока ваши маркетинговые материалы уже работают.",
        },
      ],
    },
    eph: {
      overline: "Производственный партнёр",
      location: "Китай",
      headline: "Крупнейший в Китае консолидированный хаб устойчивого ювелирного производства и экологичной гальваники",
      body: "Каждый производственный заказ через Caratzon выполняется на консолидированном заводе EPH — литьё, закрепка камней, полировка и экогальваника под одной крышей, с полным соблюдением экологических норм и прослеживаемостью на каждом этапе.",
      attrs: [
        {
          label: "Устойчивая гальваника",
          body: "Линии экологичного покрытия с низким уровнем выбросов — золочение, родирование и финишная обработка сплавами с верифицированным сокращением химических отходов и замкнутой системой водооборота.",
        },
        {
          label: "Консолидированный завод",
          body: "Литьё, закрепка камней, полировка и гальваника под одной крышей. Меньше передач между операциями, жёсткие допуски, короткие сроки исполнения.",
        },
        {
          label: "Прямая интеграция",
          body: "Утверждённые дизайны на Caratzon автоматически поступают в производственную очередь EPH. Никакого повторного поиска поставщиков, никакой разрозненной цепочки поставок.",
        },
        {
          label: "Полная прослеживаемость",
          body: "Каждый этап производства фиксируется и отображается в вашем дашборде Caratzon — от получения сырья до финального контроля качества.",
        },
      ],
    },
    forWhom: {
      overline: "Для кого эта платформа",
      heading: "Создана для\nторгового покупателя.",
      items: [
        {
          title: "Ювелирные ретейлеры",
          desc: "Закупайте изделия прямо от дизайнеров — то, чего нет у конкурентов. Каждая лицензия включает Essentials Kit — каталожные снимки, характеристики и SEO-тексты, готовые мгновенно. Обновитесь до Caratzon Kit для лайфстайл-фотографий и контента соцсетей. Добавьте на сайт, внесите в каталог и опубликуйте в Instagram в тот же день, когда оформите лицензию.",
        },
        {
          title: "Владельцы бутиков",
          desc: "Забудьте о затратах от $1 500 на NRE (единовременные инженерные расходы) за каждый дизайн. Лицензируйте проверенные, готовые к производству оригиналы. Сгенерируйте Caratzon Kit и разместите новинки на Shopify в тот же день, когда подписали лицензию.",
        },
        {
          title: "Мультибрендовые дистрибьюторы",
          desc: "Закупайте у десятков независимых дизайнеров со всего мира через единый профиль поставщика. Автоматически создавайте идеально оформленные PDF-прайс-листы для оптовых клиентов — поделитесь с розничными партнёрами мгновенно.",
        },
      ],
    },
    caraAI: {
      overline: "Cara AI · Инженерный интеллект",
      heading: "AI, который проектирует\nдля производственного цеха.",
      body: "Cara не создаёт фантастические рендеры. Он проектирует украшения, которые выдержат литьё по выплавляемым моделям, закрепку камней и восемь часов на руке человека — и генерирует полную спецификацию материалов прежде, чем вы допьёте кофе.",
      stats: [
        { value: "±0.15мм", label: "Компенсация усадки по каждому сплаву" },
        { value: "0.6мм", label: "Минимальная толщина стенки" },
        { value: "4 крапана+", label: "Инженерное удержание камней" },
        { value: "R≥0.4мм", label: "Радиус кромок для комфорта весь день" },
      ],
      cta: "Попробовать Cara AI — живое демо",
      ctaSecondary: "Подробнее",
    },
    inspirePreview: {
      overline: "Откройте для себя Inspire",
      heading: "Листайте. Сохраняйте. Закупайте.",
      body: "Добро пожаловать в Caratzon Inspire — ваша B2B-лента открытий. Просматривайте отобранный поток лучших в мире 3D CAD-дизайнов. Создавайте мудборд вашего бренда, следите за любимыми независимыми авторами и переходите от вдохновения к производству в один клик.",
      cta: "Открыть Inspire",
    },
    designerBanner: {
      overline: "Для дизайнеров",
      heading: "Ваши CAD-файлы заслуживают\nлучшего портфолио.",
      body: "Присоединяйтесь к нашей отобранной сети 3D-авторов со всего мира. Загрузите готовые к производству дизайны и автоматически получите премиальное портфолио Caratzon Inspire. Позвольте AI бесплатно визуализировать ваши файлы, показывайте работы тысячам верифицированных розничных покупателей и зарабатывайте высокомаржинальные роялти.",
      cta: "Создать портфолио дизайнера",
    },
    footerCta: {
      overline: "Готовы к закупкам",
      heading: "Ваша следующая коллекция.\nОдна платформа. Никаких пробелов.",
      cta: "Открыть торговый каталог",
      tradeOnly: "Только для торговых аккаунтов",
    },
  },

  // ─── For Designers page ──────────────────────────────────────────────────
  forDesigners: {
    meta: {
      title: "Дизайнерам · Caratzon",
      description:
        "Ваша история, рассказанная AI. Ваши дизайны — на весь мир. Присоединяйтесь к Caratzon Inspire: загрузите CAD, ответьте на несколько вопросов о своём мастерстве, и AI напишет вашу историю. Зарабатывайте 80% от каждого лицензионного взноса с бессрочными роялти.",
    },
    hero: {
      overline: "Для независимых ювелирных дизайнеров",
      h1Line1: "Ваша история.",
      h1Line2: "Рассказана красиво.",
      tagline:
        "Ответьте на несколько вопросов о своём мастерстве, источниках вдохновения и видении. Наш AI превращает ваши ответы в убедительный авторский нарратив — и ставит вашу работу перед глазами покупателей со всего мира.",
      cta: "Стать источником вдохновения →",
    },
    stats: {
      share: "Ваша доля от каждого лицензионного взноса",
      ipProtected: "IP защищена",
      cadNote: "CAD никогда не передаётся покупателям",
      perpetual: "Бессрочные",
      royaltiesNote: "Роялти с каждой произведённой единицы",
      zeroMarketing: "Нулевые расходы на маркетинг",
      portfolioNote: "AI создаёт вашу историю",
    },
    pain: {
      overline: "Реальность",
      p1: "Вы — талантливый ювелирный дизайнер. Ваши изделия оригинальны, безупречно проработаны и готовы к производству.",
      p2: "Но чтобы зарабатывать на жизнь, вы вынуждены быть менеджером в соцсетях, копирайтером, лайфстайл-фотографом и посредником с заводами. Ваши лучшие работы лежат на жёстком диске, потому что у вас нет времени и ресурсов, чтобы представить их мировым B2B-брендам.",
      quote: "Мы создали Caratzon, чтобы самые талантливые ювелирные дизайнеры мира могли сосредоточиться на том, что умеют лучше всего, — на дизайне.",
      quoteAttribution: "— Команда основателей Caratzon",
    },
    inspire: {
      overline: "Стать источником вдохновения",
      subtitle: "Ваша история, созданная AI.",
      heading: "Семь вопросов.\nОдна незабываемая история.",
      p1: "Во время регистрации мы спрашиваем о вашем пути, философии, источниках вдохновения и эмоциональном мире, стоящем за вашими работами. Не анкета — разговор о том, кто вы как автор.",
      p2: "Наш AI берёт ваши ответы и создаёт дизайнерский профиль уровня журнала — заголовок, нарратив, тематические разделы и ключевые слова голоса бренда. История, от которой покупатель перестаёт листать ленту и выходит на связь.",
      previewLabel: "Предпросмотр, созданный AI",
      previewTitle: "Напряжение — Мир Арии",
      previewBody: "Она проектирует украшения так, как архитекторы думают о свете — не чтобы украсить, а чтобы раскрыть.",
      previewExtended:
        "В залитой солнцем студии с видом на Босфор Aria Atelier работает на пересечении точности и дикости. Её изделия начинаются как акварельные наброски на фактурной бумаге — ей нужно почувствовать вес, прежде чем прикоснуться к экрану…",
      previewKeywords: ["напряжение", "золото", "архитектурность", "wabi-sabi", "тихая уверенность", "свет Босфора"] as readonly string[],
      cta: "Стать источником вдохновения →",
      features: [
        {
          label: "Ваше происхождение, ваш голос",
          description: "Мы спрашиваем, как вы стали дизайнером, какие материалы вас вдохновляют и какие культурные влияния формируют вашу эстетику. Ваши слова становятся основой вашей публичной истории.",
        },
        {
          label: "Философия и внутренний мир",
          description: "Что должен чувствовать человек, надевая ваши украшения? Какие творческие противоречия определяют ваши работы? Эти глубокие вопросы дают AI психологическую фактуру, чтобы написать то, что звучит как вы, а не как шаблон.",
        },
        {
          label: "Публикация на Inspire",
          description: "Ваша история, созданная AI, появляется на вашей странице автора в Inspire — публичном профиле, который могут найти покупатели, пресса и партнёры. Редактируйте в любое время из дашборда Studio.",
        },
      ],
    },
    designOfWeek: {
      overline: "Дизайн недели",
      subtitle: "Глобальный показ. Еженедельный выбор.",
      heading: "Попадите в\nДизайн недели.",
      badgeLabel: "Дизайн недели",
      featuredTitle: "Кольцо «Призматические лепестки»",
      featuredAttribution: "by Aria Atelier · В эфире с 3 марта 2026",
      p1: "Каждую неделю редакционная команда Caratzon выбирает один выдающийся дизайн и выводит его на первый план по всей платформе. Избранные дизайнеры получают приоритетное место на главной странице Inspire, специальный редакционный материал и прямую видимость для всей нашей сети покупателей.",
      p2: "Дизайн недели — это не просто охват, это сигнал покупателям, что ваша работа отмечена самыми взыскательными кураторами отрасли. По итогам прошлых выборов были заключены лицензионные сделки, получена пресс-поддержка и установлены долгосрочные партнёрства с брендами.",
      benefits: [
        {
          label: "Место на главной странице",
          description: "Ваш дизайн и история на протяжении семи дней занимают главное место на главной странице Inspire — первое, что видит каждый покупатель, заходя на платформу.",
        },
        {
          label: "Редакционный материал",
          description: "Специальная редакционная статья, созданная на основе вашей AI-истории и дополненная контекстом об источниках вдохновения дизайна, материалах и производственном подходе.",
        },
        {
          label: "Оповещение для сети покупателей",
          description: "Каждый активный покупатель платформы получает кураторское уведомление с вашим дизайном. Прямой канал к лицензионным переговорам.",
        },
      ],
    },
    platform: {
      overline: "Как платформа работает для вас",
      subtitle: "Минимум усилий. Максимальный охват.",
      items: [
        {
          heading: "AI рассказывает вашу историю",
          body: "Ответьте на вопросы о своём мастерстве во время регистрации. Наш AI пишет ваш авторский нарратив, генерирует ключевые слова голоса бренда и публикует вашу страницу в Inspire — живое портфолио, которое развивается вместе с вашими работами.",
        },
        {
          heading: "Прямой доступ к мировым покупателям",
          body: "Ваше автоматизированное портфолио попадает к менеджерам по производству и байерам крупных розничных брендов, которые активно ищут новинки. Вам не нужно их искать — они сами просматривают нашу кураторскую витрину и находят вас.",
        },
        {
          heading: "Никаких проблем с производством",
          body: "Когда бренд покупает ваш дизайн, вы не общаетесь с заводом. Платформа безопасно передаёт ваш файл проверенным производственным партнёрам. Литьё, контроль качества и логистика — наша забота.",
        },
        {
          heading: "Железная защита IP",
          body: "Ваш CAD-файл никогда не передаётся покупателям — ни при каком типе лицензии. Бренды получают только права на производство. Исходные файлы поступают исключительно к нашим проверенным производственным партнёрам под NDA. Только полная передача IP предполагает отчуждение дизайна.",
        },
      ],
    },
    collections: {
      overline: "Коллекции + AI-сторителлинг",
      subtitle: "Ваши дизайны заслуживают большего, чем просто сетка.",
      heading: "Объедините изделия.\nAI расскажет историю.",
      p1: "Выберите два или более опубликованных дизайна, и наш AI найдёт, что их объединяет — общие материалы, настроение, творческое чутьё — и создаст вокруг этой нити полноценную презентацию.",
      p2: "Бренды видят не просто отдельные изделия. Они видят дизайнера с цельным творческим видением и телом работ, которые держатся вместе. Именно это заставляет их выйти на связь.",
      features: [
        {
          label: "Единый голос для каждого изделия",
          description: "AI находит творческую нить, пронизывающую ваши дизайны — общую палитру, настроение, источники вдохновения — чтобы покупатели видели цельное видение, а не случайный набор файлов.",
        },
        {
          label: "Ваши изделия рассказывают одну историю",
          description: "Единый нарратив, который называет каждый дизайн и проводит связи между ними. Покупатели понимают замысел вашей коллекции ещё до того, как напишут запрос.",
        },
        {
          label: "Каждая коллекция выглядит как в Vogue",
          description: "Витрина журнального уровня с последовательным редакционным шаблоном — обложка, презентация изделий и единый визуальный стиль для всех коллекций.",
        },
      ],
      cta: "Открыть коллекции в Studio →",
    },
    badges: {
      premium: "PREMIUM",
    },
    revenue: {
      overline: "Ваш доход — три способа",
      subtitle: "Три способа получить оплату",
      heading: "Бренды покупают тремя способами.\nВы зарабатываете тремя способами.",
      footnote:
        "Вы сохраняете 80% от каждого лицензионного взноса во всех трёх моделях. Caratzon удерживает 20% для покрытия операционных расходов платформы, маркетинга и координации производства. Стандартная лицензия и Сезонное эмбарго приносят вам бессрочные роялти с каждой произведённой единицы — ваш дизайн продолжает приносить доход ещё долго после первоначальной сделки. Полная передача исключительных прав — это чистый единовременный выкуп IP: без текущих роялти, только единый премиальный платёж по минимальной цене, которую устанавливаете вы.",
      models: [
        {
          tier: "Standard",
          label: "Неисключительная производственная лицензия",
          heading: "Стандартная лицензия",
          description:
            "Бренд лицензирует право производить ваш дизайн через производственную сеть Caratzon. Неисключительная — несколько брендов могут одновременно держать стандартную лицензию. Вы получаете 80% от каждого лицензионного взноса и бессрочные роялти с каждой произведённой единицы.",
          benefit: "80% вам · Бессрочные роялти · Дизайн остаётся на рынке",
        },
        {
          tier: "Premium",
          label: "12-месячные эксклюзивные права на производство",
          heading: "Сезонное эмбарго",
          description:
            "Бренд платит премию, чтобы наложить эмбарго на ваш дизайн на 12 месяцев. В период их кампании изделие скрыто с маркетплейса — ни один конкурент не может его произвести. Вы получаете 80% от платы за эмбарго плюс бессрочные роялти с каждой произведённой единицы. По окончании срока ваш дизайн возвращается на открытый рынок.",
          benefit: "80% вам · Повышенный аванс · Ограниченный срок · Полностью повторяемо",
        },
        {
          tier: "Vault",
          label: "Полная передача исключительных прав",
          heading: "Полная передача исключительных прав",
          description:
            "Бренд приобретает постоянное право собственности и получает исходные производственные файлы (.STL / .3DM) напрямую. Дизайн навсегда снимается с маркетплейса. Вы получаете 80% единовременной премии, минимум которой устанавливаете сами. Чистый выход: один платёж, дизайн выведен, никаких дальнейших обязательств.",
          benefit: "80% вам · Вы устанавливаете минимум · Единовременный чистый выход",
        },
      ],
    },
    steps: {
      overline: "От регистрации до дохода",
      subtitle: "Три шага",
      items: [
        {
          heading: "Стать источником вдохновения",
          body: "Ответьте на вопросы о своём мастерстве, философии и источниках вдохновения. Наш AI генерирует ваш авторский нарратив и публикует страницу в Inspire — ваш публичный профиль, который находят покупатели. Загрузите первый CAD для завершения портфолио.",
        },
        {
          heading: "AI создаёт ваш бренд",
          body: "Наша Story Lab генерирует лукбук-фотографии студийного уровня и редакционный нарратив для каждого изделия. Объедините дизайны в коллекции — AI напишет единую историю для всех. Попадите в Дизайн недели для максимального охвата.",
        },
        {
          heading: "Получайте доход",
          body: "Бренды находят ваши работы и запрашивают лицензию. Вы подтверждаете или делаете встречное предложение. После подтверждения производство запускается автоматически — 80% лицензионного взноса зафиксированы до того, как будет отлит первый грамм, а роялти начисляются с каждой произведённой единицы.",
        },
      ],
    },
    bottomLine: {
      overline: "Главное",
      p1: "Ваша задача — создавать. Наш AI рассказывает вашу историю. Наша платформа продаёт ваши работы.",
      p2: "От первого вопроса при регистрации до первой лицензионной сделки — мы берём на себя нарратив, фотографию, поиск покупателей и производство. Вы просто проектируете.",
      shareLabel: "ВАШЕ",
      shareNote:
        "80% от каждого лицензионного взноса. Бессрочные роялти за единицу по стандартным сделкам и сделкам Сезонного эмбарго. Единовременная премия, минимум которой вы устанавливаете сами, при полной передаче исключительных прав. 20% Caratzon финансируют маркетинговый движок, который продаёт ваши работы, пока вы спите.",
    },
    cta: {
      overline: "Стать источником вдохновения",
      heading: "Ваша история.\nВаш прожектор.",
      primary: "Стать источником вдохновения →",
      secondary: "Уже дизайнер? Открыть Studio →",
    },
  },

  // ─── For Brands page ─────────────────────────────────────────────────────
  forBrands: {
    meta: {
      title: "Для брендов · Caratzon",
      description:
        "Закупайте оригинальные ювелирные дизайны от независимых мастеров — каждая лицензия включает Essentials Kit с каталожными снимками, характеристиками и SEO-описанием. Обновитесь до Caratzon Kit для AI-генерированной lifestyle-фотографии, лайн-шитов и контента для соцсетей. Выходите на рынок в день оформления лицензии.",
    },
    howItWorks: {
      meta: {
        title: "Как это работает — Caratzon для брендов",
        description: "От поиска до доставки — просматривайте оригиналы дизайнеров, выбирайте уровень лицензии и доверьте производство нашей сети, пока маркетинговые материалы публикуются мгновенно.",
      },
      headline: "От поиска\nдо доставки.",
      intro: "Просматривайте верифицированные оригиналы дизайнеров. Выбирайте лицензию. Производство ведётся через нашу сеть, а маркетинговые материалы публикуются мгновенно. Вот как именно это работает.",
    },
    hero: {
      overline: "Для ювелирных брендов · Бутиков · Дистрибьюторов",
      h1Line1: "Ваша следующая коллекция.",
      h1Line2: "Без R&D. Без агентств.",
      h1Line3: "Без управления производством.",
      tagline:
        "Просматривайте оригинальные дизайны независимых мастеров. Лицензируйте то, что вам нравится — каталожные снимки, характеристики и маркетинговые материалы прилагаются к каждой позиции. Производство осуществляется через нашу сеть EPH. От закупки до полки — одна платформа.",
      ctaPrimary: "Просмотреть торговый каталог",
      ctaSecondary: "Как это работает →",
    },
    marquee: {
      ariaLabel: "Предварительный просмотр каталога",
    },
    badges: {
      fullIp: "ПОЛНЫЕ ПРАВА",
    },
    stats: {
      tiers: "Уровни приобретения",
      designerDirect: "Напрямую от дизайнера",
      noIntermediaries: "Без посредников",
      contentReady: "Маркетинг включён",
      contentReadySub: "Essentials Kit с каждой лицензией · доступно обновление до Caratzon Kit",
      productionManaged: "Производство управляется",
      endToEnd: "Полный цикл",
    },
    acquire: {
      overline: "Модели приобретения",
      subtitle: "Ваша коллекция — ваши условия",
      heading: "Три способа приобрести\nдизайн Caratzon.",
      models: [
        {
          tier: "Стандартная",
          label: "Активационный и ресурсный сбор",
          heading: "Стандартная лицензия",
          description:
            "Это не просто разрешение — это ценнейший инструмент, многократно окупающий свою стоимость. Вы приобретаете дизайн, готовый к производству, и Essentials Kit: каталожные снимки, SEO-описание продукта и точные технические характеристики — всё необходимое, чтобы разместить украшение в вашем магазине в тот же день подписания. Обновитесь до Caratzon Kit для lifestyle-фотографии, лайн-шитов и контента для соцсетей.",
          gets: [
            "Essentials Kit включён — каталожные снимки, SEO-история продукта и характеристики для вашего магазина, готовы в тот же день",
            "Доступно обновление до Caratzon Kit — lifestyle-фотография, лайн-шиты и контент для соцсетей",
            "Мгновенный доступ к производству — дизайн сразу поступает в производственный конвейер",
            "Нулевые расходы на R&D — предварительно разработанные CAD-файлы, протестированные для безупречного массового производства",
            "Коммерческие права — продавайте под собственным брендом без ограничений по времени",
          ],
          benefit: "Essentials Kit включён · Без R&D · Запуск в тот же день",
        },
        {
          tier: "Эмбарго",
          label: "Эксклюзивные права на производство на 12 месяцев",
          heading: "Сезонное эмбарго",
          description:
            "Зафиксируйте дизайн для вашей следующей кампании. Эксклюзивные права на производство на 12 месяцев — ваш продукт скрыт с маркетплейса до истечения срока эмбарго. Ни один конкурент не сможет произвести его в течение вашего периода. По истечении эмбарго дизайн возвращается на рынок.",
          gets: [
            "Эксклюзивная блокировка — нулевое производство у конкурентов в течение периода",
            "Все преимущества стандартной лицензии включены",
            "Essentials Kit включён — эксклюзивная каталожная фотография и история, только для вас на 12 месяцев. Доступно обновление до Caratzon Kit",
            "Дизайн скрыт с маркетплейса на весь срок эмбарго",
          ],
          benefit: "Защита от конкурентов · Эксклюзивный контент · Потенциал флагманского продукта",
        },
        {
          tier: "Хранилище",
          label: "Полные эксклюзивные права",
          heading: "Полные эксклюзивные права",
          description:
            "Владейте интеллектуальной собственностью навсегда. Приобретите постоянное право собственности — дизайн немедленно удаляется с маркетплейса. Производственные файлы (.STL / .3DM) передаются непосредственно вам после оплаты. Ни один другой бренд никогда не сможет закупить этот дизайн. Это единственный сценарий, при котором исходные файлы передаются.",
          gets: [
            "Постоянное право собственности на IP — дизайн выводится из каталога навсегда",
            "Производственные файлы (.STL / .3DM) передаются непосредственно вам",
            "Caratzon Kit включён — откажитесь от агентства. Генерируйте неограниченное количество кастомных lifestyle-фотографий, лайн-шитов и наборов для соцсетей с помощью AI, адаптированных под ваш бренд",
            "Нижняя ценовая граница устанавливается дизайнером — переговоры начинаются с неё",
          ],
          benefit: "Постоянная эксклюзивность · Caratzon Kit включён · CAD-файлы на руках",
        },
      ],
      whyStandard: {
        label: "Почему платный стандарт лицензии?",
        heading: "Вы платите не за разрешение. Вы покупаете колоссальное преимущество.",
        points: [
          {
            heading: "Забудьте о счёте за NRE",
            body: "В традиционном производстве один дизайн обходится в $500–$2 000+ за разработку 3D, восковые отпечатки, тестовые отливки и доработки — прежде чем произвести хоть одну единицу. Стандартная лицензия заменяет всё это — файл уже разработан и прошёл стресс-тестирование.",
          },
          {
            heading: "Essentials Kit — разместите в магазине уже сегодня",
            body: "Каждая стандартная лицензия включает Essentials Kit: высококачественные каталожные снимки, SEO-оптимизированные описания продуктов и точные технические характеристики — формируются в момент оформления лицензии. Разместите украшение в вашем магазине в тот же день подписания. Когда будете готовы к полному творческому пакету, обновитесь до Caratzon Kit: AI-генерированная lifestyle-фотография, брендированные лайн-шиты и готовые к публикации материалы для соцсетей.",
          },
          {
            heading: "Под белой маркой — без правовых рисков",
            body: "Вы получаете полные коммерческие права на продажу под собственным брендом и в собственной упаковке. Дизайнер соглашается не предъявлять вам претензий по авторскому праву. Стройте свой бизнес на их творчестве — без рисков.",
          },
        ],
      },
      ipNotes: {
        standardLabel: "Стандартная лицензия и сезонное эмбарго",
        standardBody:
          "Производственные файлы хранятся на серверах Caratzon и передаются только нашим проверенным производственным партнёрам под NDA. Вы никогда не получаете исходный CAD — производство автоматически направляется через нашу сеть.",
        vaultLabel: "Полные эксклюзивные права",
        vaultBody:
          "После оплаты производственные файлы .STL / .3DM дизайнера передаются непосредственно вам. Дизайн навсегда выводится из каталога — ни один другой бренд никогда не сможет его закупить.",
      },
    },
    process: {
      overline: "Как это работает",
      subtitle: "От поиска до доставки",
      items: [
        {
          heading: "Найдите",
          body: "Просматривайте верифицированные оригиналы дизайнеров. Оценивайте Essentials Kit для каждой позиции — каталожные снимки, характеристики, редакционную историю и интерактивный 3D-просмотрщик — изучите полный пакет до принятия решения.",
        },
        {
          heading: "Лицензируйте",
          body: "Выберите уровень лицензии. Подайте запрос на закупку, согласуйте условия напрямую с дизайнером и зафиксируйте лицензию. Всё задокументировано и юридически структурировано.",
        },
        {
          heading: "Производите",
          body: "Ваш заказ направляется в нашу производственную сеть — литьё, закрепка, контроль качества и логистика управляются в полном цикле. Пока идёт производство, ваш Essentials Kit уже активен в вашем магазине. Обновитесь до Caratzon Kit — lifestyle-снимки, лайн-шиты и контент для соцсетей также публикуются сразу. Продвигайте продукт ещё до прихода товара.",
        },
      ],
      quote: "«Закупайте оригинальное. Производите в масштабе.\nФабричные контакты не нужны.»",
      bullets: [
        "Проверенная сеть производителей в Азии и Европе",
        "Литьё, закрепка, контроль качества и логистика в полном цикле",
        "Essentials Kit прилагается к каждой лицензии — каталожные снимки, характеристики и SEO-тексты",
        "Обновление до Caratzon Kit: AI-lifestyle-фотография, брендированные лайн-шиты и материалы для соцсетей по запросу",
      ],
      teaserItems: [
        { heading: "Найдите", body: "Просматривайте верифицированные оригиналы дизайнеров с полным предпросмотром." },
        { heading: "Лицензируйте", body: "Выберите уровень лицензии и зафиксируйте коммерческие права." },
        { heading: "Производите", body: "Производство управляется в полном цикле, пока ваши материалы публикуются." },
      ],
      teaserCta: "Как это работает →",
    },
    vs: {
      overline: "По сравнению с традиционным оптом",
      traditional: "Традиционный",
      caratzon: "Caratzon",
      rows: [
        ["Происхождение", "Неизвестное происхождение, многоуровневая цепочка", "Напрямую от верифицированного независимого дизайнера"],
        [
          "Затраты на R&D / NRE",
          "$500–$2 000+ за дизайн (3D-дизайнер, восковой отпечаток, тестовая отливка, доработки)",
          "Нулевые — предварительно разработано и проверено для безупречного массового производства",
        ],
        [
          "Время выхода на рынок",
          "Недели разработки и семплирования прежде чем разместить",
          "Лицензируйте сегодня, размещайте в магазине сегодня — Essentials Kit включён мгновенно",
        ],
        [
          "Маркетинговые материалы",
          "Нанять фотографа, стилиста и копирайтера — недели и тысячи долларов на коллекцию",
          "Essentials Kit включён мгновенно. Caratzon Kits, сгенерированные AI (lifestyle, соцсети, лайн-шиты), доступны по запросу",
        ],
        [
          "Коммерческие права",
          "Согласовываются отдельно, часто неоднозначны",
          "Права на белую марку включены — продавайте под собственным брендом без ограничений",
        ],
        [
          "Эксклюзивность",
          "Нет механизма блокировки конкурентов",
          "Стандартная, сезонное эмбарго (12 месяцев) или полные эксклюзивные права (постоянные)",
        ],
        [
          "Производство",
          "Управляйте фабричными контактами самостоятельно",
          "Автоматически направляется через нашу производственную сеть",
        ],
      ],
    },
    studio: {
      overline: "Caratzon Studio",
      heading: "Закупите дизайн.\nМы создадим маркетинг.",
      intro:
        "Ранее закупка новых товаров означала ожидание образцов неделями только для съёмки фотографий. С Caratzon Studio ваши маркетинговые материалы генерируются в тот же момент, когда вы оформляете лицензию. Выберите стратегию запуска:",
      essentials: {
        heading: "Essentials Kit",
        badge: "Включён в каждую лицензию",
        body: "Забудьте о ручном вводе данных. Получайте готовые к производству характеристики, чёткие каталожные снимки и SEO-тексты, мгновенно генерируемые в момент оформления лицензии. Идеальная база для размещения нового товара в вашем магазине уже сегодня.",
        features: [
          "Высококачественные извлечения на белом фоне для e-commerce",
          "SEO-оптимизированные описания продуктов",
          "Точные технические и материальные характеристики",
        ],
      },
      caratzonKit: {
        heading: "Caratzon Kit",
        badge: "Премиальное обновление для запуска",
        body: "Забудьте о фотосессии. Замените целое креативное агентство одним нажатием. Ваш логотип, фирменные цвета и эстетика органично интегрируются в lifestyle-снимки, лайн-шиты и наборы для соцсетей.",
        features: [
          "AI-генерированная lifestyle-фотография и съёмка на модели",
          "Автоматически форматированные PDF-лайн-шиты для опта в стиле вашего бренда",
          "Готовые к публикации материалы для соцсетей (Reels/TikTok-кадрирование и подписи)",
        ],
      },
    },
    caraAI: {
      overline: "Cara AI · Инженерный интеллект",
      heading: "Не инструмент генерации изображений.\nИскусственный интеллект для ювелирной инженерии.",
      intro: "Большинство AI создаёт красивые картинки. Cara создаёт дизайны, способные пройти производственный цикл и правильно ощущаться на руке. Результат — не мудборд, а производственный чертёж с полной спецификацией материалов.",
      pillars: [
        {
          stat: "±0,15 мм",
          label: "Компенсация усадки",
          body: "Cara учитывает коэффициенты усадки, специфичные для каждого металла при литье по выплавляемым моделям — золото, серебро, платина, латунь — чтобы готовое изделие соответствовало цифровой модели, а не уменьшенному приближению.",
        },
        {
          stat: "0,6 мм",
          label: "Мин. толщина стенки",
          body: "Каждая генерируемая форма соблюдает минимальные толщины стенок, специфичные для сплава — 0,6 мм для платины, 0,8 мм для золота и серебра. Никаких сверхтонких крапанов. Никаких обваливающихся рундистов. Дизайны конструктивно надёжны ещё до поступления в литьё.",
        },
        {
          stat: "4 крапана+",
          label: "Картографирование допусков вставки",
          body: "Cara рассчитывает расположение крапанов, глубину посадки и зазор жирандоли для каждого типа и огранки камня. Закрепки спроектированы для надёжного удержания при ежедневной носке — а не только для визуальной правдоподобности.",
        },
        {
          stat: "R≥0,4 мм",
          label: "Носибельность и тактильные ощущения",
          body: "Cara оценивает профиль шинки, внутреннюю кривизну, распределение веса и радиусы кромок, чтобы каждое изделие ощущалось так же хорошо, как выглядит. Острые края, верхняя тяжесть и точки зажима помечаются ещё до того, как дизайн покидает экран.",
        },
      ],
      capabilities: [
        { label: "Механика литья", detail: "Размещение литников, размер питателей и анализ потока для безупречного литья" },
        { label: "Структурный анализ", detail: "Проверка толщины стенок и распределения нагрузки по всей форме под стресс-тестом" },
        { label: "Спецификация материалов", detail: "Точный вес металла, количество камней и оценка стоимости с этапа концепции" },
        { label: "Интеллект сплавов", detail: "Материалоспецифические свойства для золота 10K–24K, серебра 925, платины 950 и латуни" },
        { label: "Производственная готовность", detail: "Каждый результат оценивается по реальным производственным допускам EPH" },
        { label: "Мост от эскиза к CAD", detail: "Загрузите набросок на салфетке — Cara извлекает геометрию и инженерно дорабатывает для производства" },
      ],
      closing: "Концепции, разработанные Cara, спроектированы так, чтобы отлиться правильно с первого раза — без доработок, без испорченного воска, без отбраковки на производстве. Когда покупатель видит концепт Cara, он смотрит на изделие, уже прошедшее стресс-тест с теми же ограничениями, которые предъявит его производитель. Вот разница между генеративным AI и инженерным AI.",
      tryCara: "Попробовать Cara AI — живое демо",
      learnMore: "Узнать больше",
    },
    cta: {
      overline: "Начать закупку",
      heading: "Просмотрите\nторговый каталог.",
      body: "Каждая стандартная лицензия включает Essentials Kit — каталожные снимки, характеристики и SEO-тексты, готовые мгновенно. Обновитесь до Caratzon Kit для lifestyle-фотографии, лайн-шитов и контента для соцсетей. Просматривайте, оценивайте в 3D и выходите на рынок быстрее, чем при любом традиционном подходе.",
      primary: "Просмотреть дизайны",
      secondary: "Как это работает",
      tradeOnly: "Только для торговых аккаунтов",
    },
  },

  // ─── For Manufacturers page ──────────────────────────────────────────────
  forManufacturers: {
    meta: {
      title: "Производство · Caratzon",
      description:
        "Каждый заказ Caratzon производится на EPH — крупнейшем консолидированном ювелирном производственном хабе Китая. 5 000+ производителей, экологически сертифицированная гальваника, полная прослеживаемость от CAD до доставки.",
    },
    hero: {
      overline: "Производство · Производственный хаб EPH",
      h1Line1: "Один объект.",
      h1Line2: "Все возможности.",
      h1Line3: "5 000+ производителей.",
      tagline:
        "Каждый заказ Caratzon направляется через EPH — крупнейший консолидированный ювелирный производственный хаб Китая. 5 000+ производителей под одной крышей, лицензированная экологическая гальваника и полная прослеживаемость от CAD-файла до готового изделия.",
      cta: "Просмотреть каталог",
    },
    stats: {
      cadReady: "5 000+",
      everyOrder: "Производителей под одной крышей",
      phases: "Полная цепочка",
      milestone: "Литьё · Закрепка · Полировка · Гальваника · ОТК",
      ecoPlating: "Экологическая лицензия",
      ephCertified: "Установка очистки сточных вод",
      fullTrace: "Полная прослеживаемость",
      clientVis: "От воскового отпечатка до доставки",
    },
    orderContents: {
      overline: "Как создаются ваши изделия",
      subtitle: "От файла до готового изделия",
      items: [
        {
          heading: "От CAD до воскового отпечатка",
          body: "Производственный файл поступает в EPH, где он преобразуется в физическую восковую мастер-модель — первый шаг от цифрового дизайна к осязаемому изделию.",
        },
        {
          heading: "Литьё и закрепка",
          body: "Литьё по выплавляемым моделям, закрепка камней и сборка выполняются на одном объекте. Никаких межфабричных передач, никакой потери допусков.",
        },
        {
          heading: "Экологическая гальваника и отделка",
          body: "Золотое, родиевое и сплавное покрытие на лицензированных экологических линиях с собственной установкой очистки сточных вод. Полировка и чистовая обработка в соответствии со спецификацией.",
        },
        {
          heading: "ОТК и доставка",
          body: "Многоточечный контроль качества перед отправкой. Производственные этапы отображаются в вашем дашборде, накладная оформляется по завершении.",
        },
      ],
    },
    workflow: {
      overline: "Производственные этапы",
      subtitle: "Ваш заказ шаг за шагом",
      phases: [
        ["Заказ подтверждён", "Ваш дизайн поступает в производственную очередь EPH"],
        ["NRE и пробная отливка", "Восковой отпечаток изготовлен, образец подан на согласование"],
        ["Литьё", "Металл залит, вес и сплав проверены"],
        ["Закрепка и сборка", "Камни закреплены, допуски проверены, компоненты собраны"],
        ["Гальваника и отделка", "Экологически сертифицированное покрытие нанесено по вашей спецификации"],
        ["ОТК и отправка", "Финальная многоточечная инспекция, накладная оформлена"],
      ],
    },
    eph: {
      overline: "Производственный хаб EPH",
      location: "Китай",
      headline: "5 000+ производителей. Одна крыша. Полная цепочка поставок.",
      body: "EPH — крупнейший консолидированный ювелирный производственный хаб Китая и эксклюзивное производственное предприятие для всех заказов Caratzon. Располагая лицензированной установкой очистки сточных вод, экологически сертифицированными линиями гальваники и полной производственной цепочкой под одной крышей, EPH устанавливает эталон устойчивого производства ювелирных изделий высокого класса в масштабе.",
      attrs: [
        {
          label: "Консолидированная цепочка поставок",
          body: "Литьё, закрепка, полировка и гальваника на одном объекте. Никакой межфабричной логистики, никаких потерь при передаче.",
        },
        {
          label: "Лицензированная экологическая гальваника",
          body: "Золотое, родиевое и сплавное покрытие на экологически сертифицированных линиях с собственной установкой очистки сточных вод — подтверждённое снижение химических выбросов.",
        },
        {
          label: "Масштаб и специализация",
          body: "5 000+ производителей с глубокой специализацией во всех ювелирных категориях — от обручальных украшений до высокой ювелирной моды.",
        },
        {
          label: "Прямая цифровая интеграция",
          body: "CAD-файлы напрямую поступают с платформы Caratzon в производственную очередь EPH — без ручного ввода, с полной прослеживаемостью.",
        },
      ],
    },
    cta: {
      overline: "Смотрите каталог",
      heading: "Узнайте, что мы\nможем произвести.",
      primary: "Просмотреть каталог",
      secondary: "Подать заявку как производитель →",
    },
    inquiryForm: {
      overline: "Заявка партнёра",
      heading: "Подайте заявку\nна вступление в производственную сеть.",
      subtitle:
        "Расскажите нам о вашем предприятии и возможностях. Наша команда по партнёрствам рассмотрит вашу заявку и ответит в течение 3 рабочих дней.",
      companyName: "Название компании / завода",
      contactName: "Имя основного контакта",
      email: "Деловой email",
      phone: "Номер телефона",
      website: "Сайт компании",
      location: "Местонахождение завода (город, страна)",
      capabilities: "Производственные возможности",
      capabilitiesHint: "Выберите все подходящие варианты",
      capabilityOptions: [
        "Литьё",
        "Закрепка камней",
        "Гальваника",
        "Полировка",
        "CAD/CAM",
        "CNC",
        "Сборка",
        "Гравировка",
        "Эмалирование",
        "ОТК / Инспекция",
      ],
      specializations: "Ювелирные специализации",
      specializationsHint: "Выберите все подходящие варианты",
      specializationOptions: [
        "Кольца",
        "Серьги",
        "Ожерелья",
        "Браслеты",
        "Обручальные украшения",
        "Мужские украшения",
        "Часы",
        "Индивидуальные изделия",
      ],
      certifications: "Сертификаты",
      certificationsHint: "Выберите все подходящие варианты",
      certificationOptions: [
        "RJC",
        "ISO 9001",
        "ISO 14001",
        "Hallmark",
        "Fairmined",
        "BSCI / Sedex",
      ],
      productionCapacity: "Ежемесячный производственный объём (изделий)",
      capacityOptions: [
        "До 500",
        "500 – 2 000",
        "2 000 – 10 000",
        "10 000 – 50 000",
        "50 000+",
      ],
      additionalInfo: "Дополнительная информация",
      additionalInfoPlaceholder:
        "Всё, что хотите добавить — специализации, сроки выполнения, текущие партнёрства и т.д.",
      submit: "Отправить заявку",
      submitting: "Отправка…",
      successHeading: "Заявка получена",
      successBody:
        "Благодарим за интерес к производственной сети Caratzon. Наша команда по партнёрствам рассмотрит вашу заявку и свяжется с вами в течение 3 рабочих дней.",
      errorRequired: "Пожалуйста, заполните все обязательные поля.",
      errorEmail: "Пожалуйста, введите действительный адрес электронной почты.",
      errorSend: "Что-то пошло не так. Пожалуйста, попробуйте снова или напишите нам напрямую.",
    },
  },

// ─── Partner Application page ────────────────────────────────────────────
partnerApplication: {
  meta: {
    title: "Заявка партнёра · Caratzon",
    description:
      "Подайте заявку на вступление в производственную сеть Caratzon. Расскажите нам о своём предприятии, возможностях и сертификатах.",
  },
},

// ─── Contact page ──────────────────────────────────────────────────────
contact: {
  meta: {
    title: "Свяжитесь с нами · Caratzon",
    description:
      "Свяжитесь с командой Caratzon. Обратитесь к нашему торговому отделу, присоединитесь как дизайнер или задайте общий вопрос.",
  },
  overline: "Контакты",
  heading: "Свяжитесь с Caratzon",
  subtitle:
    "Являетесь ли вы розничным брендом, желающим получить эксклюзивные дизайны, или инженером по 3D CAD, стремящимся монетизировать свою интеллектуальную собственность, — наша глобальная команда готова вам помочь.",
  tabs: {
    brand: "Я бренд / ритейлер",
    designer: "Я дизайнер",
    general: "Общие вопросы / Пресса / Инвесторы",
  },
  brand: {
    headline: "Свяжитесь с нашим торговым отделом",
    copy: "Готовы отказаться от разработок и оптимизировать производство? Свяжитесь с нашими экспертами по закупкам, чтобы обсудить доступ к платформе, корпоративные объёмы или производственные возможности EPH.",
    firstName: "Имя",
    lastName: "Фамилия",
    workEmail: "Рабочий адрес электронной почты",
    companyName: "Название компании / бренда",
    companyWebsite: "Сайт компании",
    helpWith: "Чем мы можем вам помочь?",
    helpOptions: [
      "Переход на тариф Source / Atelier",
      "Корпоративные закупки большого объёма",
      "Производственные возможности EPH",
      "Демонстрация Caratzon Studio AI",
      "Общий вопрос о платформе",
    ] as readonly string[],
    selectPlaceholder: "Выберите тему…",
    message: "Сообщение",
    messagePlaceholder: "Расскажите о ваших потребностях в закупках, сроках или задайте конкретные вопросы…",
    submit: "Отправить сообщение",
    submitting: "Отправка…",
  },
  designer: {
    headline: "Присоединитесь к сети дизайнеров Caratzon",
    copy: "Монетизируйте свои 3D CAD дизайны ювелирных украшений через наш глобальный лицензионный маркетплейс. Загрузите своё портфолио, установите условия — и пусть бренды сами находят вас.",
    firstName: "Имя",
    lastName: "Фамилия",
    email: "Адрес электронной почты",
    portfolioUrl: "URL портфолио / Behance / Instagram",
    cadSoftware: "Основное программное обеспечение CAD",
    cadOptions: [
      "Rhino / Grasshopper",
      "Matrix / MatrixGold",
      "ZBrush",
      "Blender",
      "SolidWorks",
      "Другое",
    ] as readonly string[],
    selectPlaceholder: "Выберите основной инструмент…",
    designCount: "Приблизительное количество дизайнов",
    designCountOptions: [
      "1–10",
      "11–50",
      "51–200",
      "200+",
    ] as readonly string[],
    designCountPlaceholder: "Выберите диапазон…",
    message: "Расскажите о своей работе",
    messagePlaceholder: "Опишите ваш стиль дизайна, специализации и то, что вы ищете в лицензионном партнёрстве…",
    submit: "Подать заявку как дизайнер",
    submitting: "Отправка…",
  },
  general: {
    headline: "Общие запросы",
    copy: "По вопросам прессы, партнёрства или отношений с инвесторами обращайтесь к нашей корпоративной команде.",
    name: "Имя",
    email: "Адрес электронной почты",
    topic: "Тема",
    topicOptions: [
      "Пресса и СМИ",
      "Отношения с инвесторами",
      "Партнёрство",
      "Другое",
    ] as readonly string[],
    selectPlaceholder: "Выберите тему…",
    message: "Сообщение",
    messagePlaceholder: "Чем мы можем вам помочь?",
    submit: "Отправить запрос",
    submitting: "Отправка…",
  },
  success: {
    heading: "Сообщение получено",
    body: "Благодарим вас за обращение. Сотрудник нашей команды ответит в течение 24 часов. А пока вы можете ознакомиться с возможностями платформы.",
    backToHome: "На главную",
    explorePlatform: "Изучить платформу →",
  },
  errors: {
    required: "Пожалуйста, заполните все обязательные поля.",
    invalidEmail: "Пожалуйста, введите корректный адрес электронной почты.",
    sendFailed: "Что-то пошло не так. Попробуйте ещё раз или напишите нам напрямую на hello@caratzon.com.",
  },
  offices: {
    heading: "Наши глобальные офисы",
    hongkong: { city: "Гонконг", label: "Торговый отдел Азиатско-Тихоокеанского региона" },
    guangdong: { city: "Гуандун", label: "Штаб-квартира производства и операций" },
    london: { city: "Лондон", label: "Европейский торговый отдел" },
  },
},

// ─── About page ──────────────────────────────────────────────────────────
about: {
  meta: {
    title: "О компании Caratzon — наша история",
    description:
      "Caratzon — глобальная двусторонняя экосистема, объединяющая независимых ювелирных дизайнеров и производство мирового класса. Узнайте, как мы трансформируем цепочку поставок ювелирной отрасли.",
  },
  hero: {
    overline: "О Caratzon · Основана в 2024",
    h1Line1: "Там, где блестящий",
    h1Line2: "дизайн встречается",
    h1Line3: "с безупречным исполнением.",
    tagline:
      "Caratzon родился из одной потерянной электронной переписки, которая едва не стоила ювелирному бренду целого праздничного сезона. Сегодня мы — глобальная двусторонняя экосистема, объединяющая блестящих независимых дизайнеров и производство мирового уровня с полным управлением.",
    lastUpdated: "Последнее обновление: февраль 2026",
  },
  stats: {
    overline: "Масштаб платформы",
    subtitle: "В цифрах",
    items: [
      { value: "10 000+", label: "Эксклюзивных CAD дизайнов" },
      { value: "150+", label: "Проверенных производственных партнёров" },
      { value: "1 млн+", label: "Управляемых производственных узлов" },
      { value: "$50 млн+", label: "Заработано создателями" },
      { value: "0", label: "Непрозрачных задержек в цепочке поставок" },
    ],
  },
  origin: {
    overline: "Наше происхождение",
    subtitle: "У каждой платформы есть своё начало",
    p1: "Каждый день Caratzon помогает создателям монетизировать своё искусство и позволяет B2B брендам закупать свежие эксклюзивные коллекции — с абсолютной уверенностью в прозрачной, узловой цепочке поставок.",
    p2: "Мы существуем, чтобы устранить риски и трудности глобальных закупок. Когда бренд влюбляется в дизайн на нашей платформе, ему не нужно думать о том, как его произвести. Caratzon берёт на себя CAD инжиниринг, физическое прототипирование, мультивендорную логистику, контроль качества и финальную доставку — от начала до конца.",
    digitalDesign: "Цифровой дизайн",
    precisionCraft: "Точное мастерство",
  },
  timeline: {
    overline: "История Caratzon",
    subtitle: "Четыре этапа",
    phases: [
      {
        heading: "Разорванная цепь",
        subheading: "Там, где начинается история",
        label: "Проблема",
        body: "Всё началось с блестяще спроектированного многокомпонентного изделия, требовавшего сапфиров индивидуальной огранки, точного литья по выплавляемым восковым моделям и специализированного родиевого покрытия. Передача CAD файлов на три разные фабрики на двух континентах привела к огромной задержке — из-за незначительного дефекта пористости, который фабрика пыталась скрыть. Стало абсолютно очевидно, что зависимость ювелирной отрасли от PDF-счетов, обновлений в WhatsApp и слепого доверия является неустойчивой.",
      },
      {
        heading: "Trapped Talent",
        subheading: "Рыночный разрыв, скрытый на виду",
        label: "Инсайт",
        body: "Одновременно мы осознали огромный разрыв на рынке. Блестящие независимые ювелирные дизайнеры хранили жёсткие диски, полные захватывающих 3D CAD моделей, но совершенно не имели фабричных связей или капитала для их производства. Между тем крупные B2B розничные бренды отчаянно искали свежую эстетику, но боялись производственных рисков, связанных с тестированием неизвестных создателей. Искусство и логистика оказались принципиально несовместимы.",
      },
      {
        heading: "Создание операционной системы",
        subheading: "Проектирование центральной нервной системы",
        label: "Разработка",
        body: "Мы решили построить идеальный мост. Мы разработали проприетарную модульную узловую платформу, которая функционирует как центральная нервная система ювелирного производства. Она была создана для того, чтобы напрямую связывать видение технического дизайнера с внешними фабриками, отслеживать маржу P&L в реальном времени и защищать каждую техническую спецификацию — при этом полностью изолируя отдельных вендоров.",
      },
      {
        heading: "Идеальный маркетплейс",
        subheading: "Дизайн встречается с безупречным исполнением",
        label: "Результат",
        body: "Caratzon официально запустился не просто как инструмент отслеживания, а как тщательно отобранный маркетплейс дизайнеров, поддерживаемый полностью управляемым производственным движком. Сегодня, когда бренд влюбляется в дизайн на нашей платформе, ему не нужно думать о производстве. Одним кликом Caratzon автоматически направляет дизайн через нашу проверенную сеть, беря на себя CAD инжиниринг, физическое прототипирование и финальный контроль качества.",
      },
    ],
  },
  vision: {
    overline: "Видение и миссия",
    visionLabel: "Наше видение",
    visionBody:
      "Стать определяющей глобальной экосистемой, где ювелирный дизайн мирового класса органично встречается с безупречным исполнением — превращая фрагментированную цепочку поставок в прозрачный, высокоприбыльный механизм для создателей и брендов.",
    missionLabel: "Наша миссия",
    missionBody:
      "Расширять возможности независимых ювелирных дизайнеров, соединяя их искусство с глобальными B2B брендами — при поддержке нашей полностью управляемой производственной операционной системы. Мы существуем, чтобы устранить риски и трудности глобальных закупок.",
  },
  howItWorks: {
    overline: "Как это работает",
    subtitle: "Один клик. От начала до конца.",
    steps: [
      {
        heading: "Дизайнер загружает",
        body: "Независимые дизайнеры загружают CAD файлы, устанавливают условия защиты IP и публикуют редакционные истории. Их работы защищены подписанными ссылками с ограниченным сроком действия.",
      },
      {
        heading: "Бренд открывает для себя",
        body: "Розничные бренды просматривают более 10 000 эксклюзивных дизайнов в тщательно отобранном каталоге, изучая изделия в интерактивном 3D-просмотрщике внутри платформы.",
      },
      {
        heading: "Caratzon направляет",
        body: "После достижения договорённости наш узловой движок автоматически направляет дизайн через проверенную производственную сеть — CAD инжиниринг, прототипирование, контроль качества.",
      },
      {
        heading: "Доставка с маржой",
        body: "Прозрачность P&L в реальном времени, отслеживание этапов и финальная доставка — без непрозрачных задержек и с полной прозрачностью цепочки поставок на всём протяжении.",
      },
    ],
  },
  leadership: {
    overline: "Руководство",
    founderRole: "Основатель и генеральный директор",
    founderName: "[Имя основателя]",
    founderBio:
      "Краткая биография в 1–2 предложениях о вашем опыте в области ювелирного дизайна, управления цепочками поставок и технологий — и о том, что движет видением Caratzon.",
    tags: ["Ювелирный дизайн", "Цепочка поставок", "Технологии"],
  },
  supplyChain: {
    overline: "Архитектура цепочки поставок",
    heading: "Узловая система.\nПолная прозрачность.",
    items: [
      {
        title: "Каждый вендор изолирован",
        desc: "Отдельные узлы фабрик работают независимо. Ни один вендор не видит спецификации, цены или данные о мощностях другого.",
      },
      {
        title: "P&L в реальном времени",
        desc: "Видимость маржи на каждом производственном этапе — дизайнеры, бренды и менеджеры видят только данные, относящиеся к их роли.",
      },
      {
        title: "Неизменяемый журнал аудита",
        desc: "Каждая версия CAD, согласование и производственное событие имеют временную метку и фиксируются в журнале — полная история происхождения у вас под рукой.",
      },
    ],
  },
  quote: {
    text: "«Искусство и логистика оказались принципиально несовместимы.\nМы построили мост.»",
    attribution: "Caratzon · Основополагающий принцип",
  },
  cta: {
    overline: "Партнёрство с Caratzon",
    heading: "Создайте будущее\nювелирного искусства.",
    primary: "Просмотреть каталог",
    secondary1: "Для брендов и ритейлеров →",
    secondary2: "Для независимых дизайнеров →",
  },
},

// ─── For Studio (marketing page) ──────────────────────────────────────────
forStudio: {
  meta: {
    title: "Caratzon Studio · Там, где AI встречается с ювелирным верстаком",
    description: "Рабочее пространство для ювелирных дизайнеров на базе AI. Познакомьтесь с Cara — вашим дизайн-ассистентом, который мыслит в миллиметрах, твёрдости по шкале Мооса и предельной прочности на разрыв.",
  },
  hero: {
    overline: "Caratzon Studio",
    h1Line1: "Ваши дизайны",
    h1Line2: "заслуживают ателье.",
    tagline: "Caratzon Studio — это рабочее пространство на базе AI, где независимые ювелирные дизайнеры загружают CAD, рассказывают свою историю и доверяют платформе всё остальное — маркетинг, лицензирование, производственную маршрутизацию и теперь умный дизайн-ассистент.",
    cta: "Подать заявку как дизайнер →",
    ctaSecondary: "Посмотреть цены →",
  },
  stats: {
    aiAssisted: "На базе AI",
    designTools: "Инструменты дизайна",
    stoneTypes: "50+",
    stoneLabel: "База данных камней и сплавов",
    phases: "6",
    phasesLabel: "Шагов от эскиза до продаж",
    renders: "Мгновенные",
    rendersLabel: "Рендеры для каталога",
  },
  cara: {
    overline: "Знакомьтесь: Cara",
    subtitle: "От эскиза до продаж",
    heading: "От наброска на салфетке\nдо глобальных продаж.",
    p1: "Cara — это AI дизайн-ассистент, который превращает нарисованный от руки эскиз в готовое к производству изделие, выставленное на рынок, — проходя каждый шаг от уточнения концепции до рендеров для каталога, сторителлинга и торгового листинга.",
    p2: "Сфотографируйте ваш эскиз. Cara задаёт правильные вопросы — материалы, размеры, расположение камней — и создаёт точный концепт, который отражает вашу оригинальную идею в каждой кривой и пропорции.",
    pipeline: [
      { num: "01", label: "Эскиз", heading: "Загрузите ваш эскиз", body: "Сфотографируйте нарисованный от руки эскиз или набросок. Визуальная модель Cara анализирует силуэты, пропорции, расположение камней и декоративные детали — извлекая ваш замысел с хирургической точностью.", icon: "pencil" },
      { num: "02", label: "Уточнение", heading: "Умные уточняющие вопросы", body: "Прежде чем изменить хотя бы один пиксель, Cara задаёт уточняющие вопросы — тип металла, огранка камней, размер кольца, предпочтения по отделке. Ваши ответы становятся дизайн-брифом, который фиксирует точность.", icon: "message-circle" },
      { num: "03", label: "Рендер", heading: "Точный AI рендер", body: "Cara создаёт фотореалистичный концепт, точно воспроизводящий ваш эскиз — тот же силуэт, те же пропорции, то же расположение камней. Дорабатывайте в диалоге, пока не добьётесь совершенства.", icon: "image" },
      { num: "04", label: "Сохранение", heading: "Сохранить в Studio", body: "Один клик сохраняет согласованный рендер в вашем Studio как новый черновик дизайна — с оригинальным эскизом, историей диалога и всеми параметрами дизайна для дальнейшей доработки.", icon: "save" },
      { num: "05", label: "История", heading: "Нарратив о создании", body: "Ваш эскиз и весь диалог с Cara становятся сырым материалом для сгенерированной AI истории о создании — compelling редакционный нарратив, прослеживающий творческий путь от первой линии до финального рендера.", icon: "book-open" },
      { num: "06", label: "Продажи", heading: "Листинг и заработок", body: "Рендеры для каталога, материалы лукбука и торговые листинги генерируются автоматически. Ваш дизайн попадает в глобальный каталог, где бренды открывают, лицензируют и заказывают его — а вы зарабатываете 80% с каждой сделки.", icon: "trending-up" },
    ],
    conversationDemo: {
      label: "Cara — от эскиза к концепту",
      userSketch: "Я нарисовала этот коктейльный перстень — можешь превратить его в реальный концепт?",
      caraAnalysis: "Вижу смелую асимметричную шинку с тем, что похоже на камень в натяжной закрепке и конусообразными плечиками. Прежде чем создать рендер, позвольте уточнить несколько моментов:",
      caraQuestions: [
        "Какой металл вы представляете? Угловые линии предполагают платину или белое золото",
        "Центральный камень — это изумрудная огранка? Приблизительно какой карат?",
        "Вижу текстурные отметки на шинке — матовая или молотковая отделка?",
      ],
      userReply: "Платина, изумруд 2 карата изумрудной огранки, матовая отделка шинки.",
      caraConfirm: "Отлично. Создаю концепт — тот же силуэт, те же пропорции, платина с матовой отделкой и изумруд 2 карата изумрудной огранки в натяжной закрепке…",
      toolLabel: "Создание точного концепта…",
    },
    cta: "Попробовать Cara в Studio →",
  },
  storyLab: {
    overline: "AI Story Lab",
    subtitle: "Автоматизированный сторителлинг",
    heading: "Ваша история,\nрассказанная красиво.",
    p1: "Ответьте на семь вопросов при онбординге — и наш AI создаёт богатую редакционную историю дизайнера, страницу профиля и лукбук — контент, на который раньше требовались копирайтер, фотограф и арт-директор.",
    p2: "Каждый дизайнер получает персональный профиль Inspire, где покупатели открывают вашу работу через AI-генерируемые нарративы, а не через стандартные описания товаров.",
    features: [
      { label: "Редакционные профили", description: "AI-генерируемые истории дизайнеров, которые читаются как материалы в журнале — не шаблонные биографии." },
      { label: "AI лукбуки", description: "Цельные визуальные нарративы по всему портфолио, автоматически создаваемые на основе ваших дизайнов." },
      { label: "Сторителлинг коллекции", description: "Группируйте изделия в тематические коллекции и позвольте AI соткать нарратив по всему набору." },
    ],
  },
  workspace: {
    overline: "Рабочее пространство дизайнера",
    subtitle: "Всё в одном месте",
    heading: "Загружайте. Управляйте.\nЗарабатывайте.",
    p1: "Рабочее пространство Studio — это ваш командный центр: загружайте CAD файлы, отслеживайте лицензионный доход, управляйте коллекциями, следите за аналитикой и общайтесь с покупателями. Всё из единой панели управления.",
    features: [
      { num: "01", heading: "3D просмотрщик", body: "Покупатели изучают ваши дизайны в полном 3D перед лицензированием. Ваши CAD файлы никогда не раскрываются — только рендеры и превью." },
      { num: "02", heading: "Панель управления доходами", body: "Отслеживайте лицензионные сборы, роялти и производственные заказы в реальном времени. 80% доля дохода с каждой лицензии." },
      { num: "03", heading: "Защита IP", body: "CAD файлы зашифрованы в хранилище и никогда не показываются покупателям. Структурированные лицензионные уровни с чёткими условиями." },
      { num: "04", heading: "Прямые сообщения", body: "Общайтесь с покупателями и производственными менеджерами непосредственно внутри платформы." },
    ],
  },
  revenue: {
    overline: "Модели дохода",
    subtitle: "Три лицензионных уровня",
    heading: "Зарабатывайте\nна своих условиях.",
    models: [
      { tier: "standard", label: "Стандартная лицензия", heading: "Неисключительная", description: "Несколько покупателей могут лицензировать один и тот же дизайн. Вы сохраняете 80% лицензионного сбора плюс бессрочные роялти с каждого производственного заказа.", benefit: "80% + Бессрочные роялти" },
      { tier: "embargo", label: "Сезонное эмбарго", heading: "12-месячная эксклюзивность", description: "Один покупатель получает защищённую от конкурентов эксклюзивность на год. Более высокий авансовый платёж. По истечении 12 месяцев дизайн возвращается в стандартный доступ.", benefit: "80% + Премиальный сбор за эксклюзивность" },
      { tier: "vault", label: "Полные исключительные права", heading: "Постоянная передача IP", description: "Единовременный выкуп с постоянной передачей IP, включая CAD файл. Самый высокий авансовый платёж. Входит Caratzon Kit.", benefit: "Полный выкуп + Caratzon Kit" },
    ],
  },
  steps: {
    overline: "Начало работы",
    subtitle: "Три шага",
    items: [
      { heading: "Заявка и онбординг", body: "Пройдите онбординг из семи вопросов. Наш AI автоматически создаёт ваш редакционный профиль, историю дизайнера и страницу Inspire." },
      { heading: "Загрузка и создание", body: "Загружайте CAD файлы. Используйте Cara для доработки дизайнов, анализа конструкции и создания рендеров для каталога. Организуйте изделия в коллекции." },
      { heading: "Заработок и рост", body: "Покупатели открывают, лицензируют и заказывают ваши дизайны. Вы зарабатываете 80% с каждой лицензии. Производством управляет наш сертифицированный EPH производственный центр." },
    ],
  },
  cta: {
    overline: "Присоединиться к Studio",
    heading: "Создавайте будущее.\nОстальное — наша забота.",
    primary: "Подать заявку как дизайнер →",
    secondary: "Изучить каталог →",
  },
},

// ─── Studio ────────────────────────────────────────────────────────────────
studio: {
  nav: { guide: "Руководство", inbox: "Входящие", storyLab: "Мои дизайны", collections: "Коллекции", cara: "Cara", publishing: "Публикация", admin: "Администратор →", catalog: "← Каталог", analytics: "Аналитика" },
  notifications: { heading: "Уведомления", empty: "Уведомлений пока нет." },
  cara: {
    title: "Cara",
    subtitle: "AI дизайн-ассистент",
    newSession: "Новая дизайн-сессия",
    placeholder: "Опишите вашу идею или загрузите изображение для вдохновения…",
    emptyTitle: "Знакомьтесь: Cara",
    emptyDescription: "Ваш AI дизайн-ассистент — отчасти мастер-ювелир, отчасти инженер-механик. Опишите вашу идею или загрузите изображение для вдохновения, чтобы начать.",
    analyzing: "Анализ вдохновения…",
    rendering: "Генерация рендера дизайна…",
    structuralAnalysis: "Запуск симуляции технологичности…",
    calculatingBom: "Расчёт спецификации материалов…",
    generatingVideo: "Создание превью с поворотом — это может занять до минуты…",
    processing: "Обработка…",
    phases: {
      spark: "Искра",
      architect: "Архитектор",
      crucible: "Горнило",
      bench: "Верстак",
    },
    artifacts: {
      title: "Артефакты",
      empty: "Артефакты появятся здесь по мере их генерации Cara.",
      moodBoards: "Мудборды",
      renders: "Рендеры дизайна",
      structuralAnalysis: "Конструктивный анализ",
      materialAnalysis: "Анализ материалов",
      bom: "Спецификация материалов",
      videos: "Видео",
    },
    sessions: {
      empty: "Сессий пока нет",
      emptyHint: "Начните вашу первую дизайн-сессию с Cara",
      untitled: "Сессия без названия",
    },
    errors: {
      streamFailed: "Что-то пошло не так. Пожалуйста, попробуйте ещё раз.",
      rateLimited: "Вы отправляете сообщения слишком быстро. Подождите немного.",
    },
  },
  gettingStarted: {
    overline: "Студия дизайнера",
    title: "Начало работы",
    subtitle: "Визуальное руководство по трём основным функциям студии — чтобы вы понимали полный рабочий процесс до начала работы.",
    howItWorks: {
      title: "Как это работает",
      steps: [
        {
          title: "Загрузка и создание",
          label: "Мои дизайны",
          body: "Загрузите ваш CAD файл и вспомогательные материалы, затем позвольте AI создать готовую к публикации историю каталога для вашего дизайна.",
        },
        {
          title: "Ответы на запросы",
          label: "Входящие",
          body: "Бренды отправляют запросы на кастомизацию. Вы сотрудничаете, согласовываете условия и загружаете исправленные CAD файлы — всё отслеживается в одном диалоге.",
        },
        {
          title: "Кураторство коллекций",
          label: "Коллекции",
          body: "Группируйте ваши опубликованные дизайны в тематические редакционные коллекции. AI соткёт их индивидуальные истории в цельный нарратив.",
        },
      ],
    },
    storyLabWorkflow: {
      title: "Рабочий процесс дизайна",
      subtitle: "Два этапа превращают ваш CAD файл в опубликованную страницу Inspire.",
      phase1: {
        title: "Этап 1 — Черновик дизайна",
        subtitle: "Пятишаговый мастер фиксирует всё, что платформа нуждается знать, прежде чем AI возьмёт управление.",
        steps: [
          { title: "Идентичность", body: "Назовите изделие и напишите краткое описание." },
          { title: "Цены и спецификации", body: "Укажите базовую лицензионную цену, вес металла и количество камней." },
          { title: "Защита IP", body: "Выберите стандартную, сезонного эмбарго или полную исключительную лицензию." },
          { title: "Загрузка материалов", body: "Загрузите производственный CAD, файл 3D просмотрщика и главный рендер." },
          { title: "Проверка и создание", body: "Подтвердите всё и создайте черновик дизайна." },
        ],
      },
      phase2: {
        title: "Этап 2 — AI история",
        subtitle: "Шесть шагов превращают ваш черновик в готовую к каталогу историю с AI-генерируемым нарративом и изображениями.",
        steps: [
          { title: "Ключевые слова вдохновения", body: "Загрузите референсные фото; AI анализирует настроение, материалы и темы." },
          { title: "Выбор ключевых слов", body: "Выберите слова, которые лучше всего передают суть вашего изделия." },
          { title: "Генерация истории", body: "Опишите материалы и замысел; AI создаёт нарратив для каталога." },
          { title: "Доработка истории", body: "Редактируйте черновик AI — этот текст читают покупатели." },
          { title: "Генерация витрины", body: "AI создаёт страницу каталога с разделами, заголовками и слотами для изображений." },
          { title: "Отправка на проверку", body: "Заблокируйте правки и отправьте на проверку команде." },
        ],
      },
    },
    afterSubmission: {
      title: "Что происходит после отправки",
      body: "Администратор проверяет вашу историю и витрину. Вы получите уведомление о решении — одобренные дизайны публикуются в торговом каталоге, а отклонённые возвращаются к вам с отзывами для доработки.",
      steps: [
        { label: "Отправлено", body: "Ваш дизайн поступает в очередь проверки." },
        { label: "На проверке", body: "Администратор оценивает качество истории и полноту материалов." },
        { label: "Опубликовано", body: "Одобренные дизайны появляются в торговом каталоге для обнаружения брендами." },
      ],
    },
    fileRequirements: {
      title: "Требования к файлам",
      subtitle: "Краткий справочник принятых форматов и ограничений по размеру.",
      columns: { asset: "Материал", formats: "Форматы", maxSize: "Макс. размер", required: "Обязательно" },
      rows: [
        { asset: "Производственный CAD", formats: ".stl, .3dm, .step", maxSize: "50 МБ", required: "Да" },
        { asset: "3D просмотрщик", formats: ".glb", maxSize: "25 МБ", required: "Да" },
        { asset: "Главный рендер", formats: ".jpg, .png, .webp", maxSize: "10 МБ", required: "Да" },
        { asset: "Референсные фото", formats: ".jpg, .png, .webp", maxSize: "5 МБ каждый", required: "Необязательно" },
      ],
    },
    ipTiers: {
      title: "Уровни IP: подробное объяснение",
      subtitle: "Три лицензионных модели — выберите ту, что соответствует вашим целям.",
      tiers: [
        {
          title: "Стандартная лицензия",
          body: "Ваш CAD остаётся у Caratzon, передаётся только проверенным производителям по соглашению о неразглашении. Вы зарабатываете 80% каждого лицензионного сбора плюс бессрочные роялти. Несколько брендов могут лицензировать одновременно.",
        },
        {
          title: "Сезонное эмбарго",
          body: "Один бренд владеет эксклюзивными правами в течение 12 месяцев. Дизайн скрыт в течение их периода и возвращается на маркетплейс по окончании эмбарго. Применяется премиальное ценообразование.",
        },
        {
          title: "Полные исключительные права",
          body: "Бренд приобретает полный IP на постоянной основе. Ваш производственный CAD передаётся непосредственно им. Единовременная высокостоимостная сделка — дизайн навсегда удаляется с маркетплейса.",
        },
      ],
    },
    cta: {
      title: "Готовы начать?",
      button: "Создать первый дизайн",
    },
  },
  storyLab: {
    overline: "Студия дизайнера",
    title: "Мои дизайны",
    subtitle: "Загружайте дизайны, создавайте AI истории и публикуйте на своей странице Inspire.",
    newDraft: "Новый черновик дизайна",
    overlineLabel: "Мои дизайны",
    cancel: "Отмена",
    continue: "Продолжить",
    back: "Назад",
    stepOf: "Шаг {current} из {total}",
    keyboardHint: "Подсказка: Alt + клавиши со стрелками для навигации по шагам",
    editing: "Редактирование…",
    draftInProgress: "Черновик в работе",
    emptyTitle: "Дизайнов пока нет",
    emptySubtitle: "Загрузите ваш первый дизайн, чтобы начать формировать портфолио Inspire.",
    newDesign: "Новый дизайн",
    emptyHint: "или выберите существующий дизайн на боковой панели",
    batchUpload: "Пакетная загрузка",
    batchUploadSubtitle: "Перетащите до 20 фотографий украшений — AI создаст название, описание и ключевые слова для каждого",
    batchProcessing: "Обработка {count} изделий…",
    batchGenerate: "Создать {count} дизайнов",
    batchCreated: "{count} дизайнов создано",
    batchDuplicates: "{count} дубликатов пропущено",
    batchPossibleDuplicates: "{count} возможных дубликатов",
    batchFailed: "{count} не удалось обработать",
    batchDuplicateExact: "Уже загружено как \"{title}\"",
    batchDuplicateNear: "Может быть похоже на \"{title}\" (совпадение {confidence}%)",
    batchViewInLab: "Просмотреть в «Мои дизайны»",
    batchDropHere: "Перетащите до 20 фотографий украшений сюда",
    batchDropHint: "AI создаст название, описание и ключевые слова для каждого изделия",
    celebrationTitle: "Черновик дизайна создан!",
    celebrationSubtitle: "Ваш дизайн готов. Начните создавать его историю с помощью генерации ключевых слов ниже.",
    selectDesign: "Выберите дизайн\nдля продолжения",
    selectDesignHint: "Выберите черновик на панели или создайте новый дизайн.",
    sidebar: { newDesign: "+ Новый дизайн", designs: "Дизайны", noDesigns: "Дизайнов пока нет" },
    steps: {
      identity: {
        title: "Назовите изделие",
        subtitle: "Дайте вашему дизайну название и краткое описание, передающее его суть. Это первое, что увидят покупатели.",
        designTitle: "Название дизайна",
        titleTip: "Это станет публичным именем на маркетплейсе Caratzon. Делайте его выразительным и запоминающимся — покупатели просматривают сначала по названию.",
        titlePlaceholder: "например, Серьги-капли Lunette",
        titleHint: "Выберите название, передающее суть вашего изделия",
        description: "Краткое описание",
        descriptionPlaceholder: "Одно или два предложения, описывающих изделие и его характер.",
        descriptionHint: "Отображается в каталоге. Делайте его выразительным и лаконичным.",
      },
      pricing: {
        title: "Цены и спецификации",
        subtitle: "Установите базовую лицензионную цену и предоставьте технические спецификации материалов. Это поможет покупателям оценить затраты, а производителям — подготовить производственные предложения.",
        basePrice: "Базовая цена (USD)",
        basePriceTip: "Это ваш лицензионный сбор. Бренды платят эту сумму за использование вашего дизайна. Вы получаете 80% после комиссий платформы.",
        basePriceHint: "Лицензионный сбор, который бренды платят за использование вашего дизайна. Вы зарабатываете 80%.",
        metalWeight: "Вес металла (граммы)",
        metalWeightTip: "Приблизительный вес помогает производителям оценить производственные затраты и предоставить точные предложения.",
        metalWeightHint: "Приблизительный вес металла в дизайне",
        stoneCount: "Количество камней",
        stoneCountHint: "Общее количество камней в закрепке изделия",
        howPricingWorks: "Как работает ценообразование:",
        howPricingBody: "Ваша базовая цена — это лицензионный сбор за дизайн. Производственные затраты рассчитываются отдельно проверенными фабриками на основе веса металла, камней и сложности.",
      },
      licensing: {
        title: "Выберите защиту IP",
        subtitle: "Выберите, как бренды могут использовать ваш дизайн. Каждый уровень предлагает различные уровни защиты и модели дохода.",
        licensingTip: "Ваш уровень IP определяет, как бренды могут использовать ваш дизайн. Стандартный допускает несколько лицензий; Исключительный даёт одному бренду временные права; Хранилище передаёт полное владение.",
        standard: "Стандартная лицензия",
        standardDesc: "Ваш CAD остаётся у Caratzon, передаётся только проверенным производителям по соглашению о неразглашении. Вы зарабатываете 80% каждого лицензионного сбора плюс бессрочные роялти.",
        standardDetail: "Лучший вариант для дизайнеров, желающих постоянного пассивного дохода. Несколько брендов могут лицензировать одновременно.",
        exclusive: "Сезонное эмбарго",
        exclusiveDesc: "Один бренд владеет эксклюзивными правами в течение 12 месяцев. Дизайн скрыт в течение их периода. Возвращается на рынок по окончании эмбарго.",
        exclusiveDetail: "Премиальное ценообразование за эксклюзивность. Бренд платит более высокий лицензионный сбор за период эмбарго.",
        vault: "Полные исключительные права",
        vaultDesc: "Бренд приобретает полный IP на постоянной основе. Ваш производственный CAD передаётся непосредственно им. Вы зарабатываете 80% от цены приобретения.",
        vaultDetail: "Единовременная высокостоимостная сделка. Дизайн навсегда удаляется с маркетплейса.",
        ipTransfer: "Передача IP",
      },
      assets: {
        title: "Загрузите ваши материалы",
        subtitle: "Загрузите файлы дизайна, необходимые покупателям и производителям. Ваш производственный CAD защищён — только проверенные производители видят его по соглашению о неразглашении.",
        assetsTip: "Ваш производственный CAD никогда не передаётся покупателям. Только проверенные Caratzon производители получают к нему доступ на строгих условиях NDA.",
        requiredFiles: "Обязательные файлы",
        optionalFiles: "Необязательно — улучшает качество AI истории",
        vaultNotice: "Полные исключительные права.",
        vaultNoticeDetail: "Ваш CAD будет передан непосредственно приобретающему бренду.",
        cadProtected: "CAD защищён как IP.",
        cadProtectedDetail: "Только проверенные Caratzon производители получают его по соглашению о неразглашении.",
        cadLabel: "Производственный CAD",
        cadLabelVault: "Производственный CAD для передачи IP",
        cadHint: ".stl · .3dm · .step — ваш исходный файл, никогда не передаётся покупателям",
        viewerLabel: "Материал для 3D просмотрщика",
        viewerHint: ".glb — экспорт из вашего CAD для интерактивного превью покупателя",
        renderLabel: "2D рендер / главное изображение",
        renderHint: ".jpg · .png · .webp — основная миниатюра каталога",
      },
      review: {
        title: "Проверка и создание",
        submit: "Создать черновик дизайна",
        submitting: "Создание…",
        identity: "Идентичность",
        pricingSpecs: "Цены и спецификации",
        licensingTier: "Лицензионный уровень",
        assets: "Материалы",
        edit: "Редактировать",
        uploaded: "Загружено",
        missing: "Отсутствует",
        allRequired: "Все обязательные файлы должны быть загружены перед созданием.",
      },
    },
    workflow: {
      featured: "Рекомендуемый",
      revisionRequired: "Требуется доработка",
      revisionHint: "Учтите отзывы выше, затем отправьте повторно.",
      noReason: "Причина не указана.",
      step01: "Ключевые слова вдохновения",
      step01Sub: "Загрузите референсные фото и добавьте контекст. AI анализирует настроение, материалы и темы для формирования палитры ключевых слов.",
      additionalContext: "Дополнительный контекст",
      contextPlaceholder: "например, влияние Ар-деко, сапфиры цвета полуночного синего…",
      generateKeywords: "Сгенерировать ключевые слова",
      regenerateKeywords: "Перегенерировать ключевые слова",
      analysingRefs: "Анализ референсов…",
      step02: "Выбор ключевых слов",
      step02Sub: "Выберите слова, которые лучше всего передают суть этого изделия — они станут основой AI-генерируемой истории.",
      saveKeywords: "Сохранить выбранные ключевые слова",
      saving: "Сохранение…",
      step03: "Генерация истории",
      step03Sub: "Опишите материалы, замысел и скрытый смысл. AI создаст нарратив, который появится на странице вашего каталога.",
      pieceDescription: "Описание изделия",
      describePlaceholder: "Опишите дизайн",
      materialsColors: "Материалы и цвета",
      materialsPlaceholder: "Чистое серебро, бриллианты в огранке павé…",
      initialSpark: "Первоначальный импульс",
      sparkPlaceholder: "Образ, воспоминание или настроение…",
      hiddenSymbolism: "Скрытый символизм",
      symbolismPlaceholder: "Какой смысл закодирован в этом изделии?",
      generateStory: "Сгенерировать историю",
      regenerateStory: "Перегенерировать историю",
      writingStory: "Написание вашей истории…",
      step04: "Доработка истории",
      step04Sub: "Проверьте черновик AI. Свободно редактируйте заголовок и основной текст — этот текст читают покупатели на странице каталога.",
      saveStory: "Сохранить историю",
      step05: "Генерация витрины",
      step05Sub: "AI создаёт страницу каталога — количество разделов, слоты для изображений и заголовки. Всё генерируется на основе вашей истории.",
      currentLayout: "Текущий макет:",
      sections: "разделов",
      generatedImages: "сгенерированное изображение",
      generatedImagesPlural: "сгенерированных изображений",
      regenerateWarning: "Перегенерация заменяет текущий макет.",
      generateShowcase: "Сгенерировать витрину",
      regenerateShowcase: "Перегенерировать витрину",
      buildingShowcase: "Создание витрины…",
      step06: "Отправка на проверку",
      step06Sub: "Блокирует дальнейшее редактирование до тех пор, пока администратор не проверит вашу историю и витрину. Вы получите уведомление о решении.",
      awaitingReview: "Ваш дизайн находится у команды проверки. Вы получите уведомление, когда будет принято решение.",
      submitForApproval: "Отправить на проверку",
      awaitingAdmin: "Ожидание проверки администратором…",
      published: "Опубликовано",
      submitting: "Отправка…",
      viewPublished: "Просмотреть опубликованную историю",
    },
    upload: {
      dragOrClick: "Перетащите сюда или",
      clickToBrowse: "нажмите для выбора",
      maxSize: "Макс. {size} МБ",
      required: "Обязательно",
      uploading: "Загрузка {name}…",
      replace: "Заменить",
      retry: "Повторить",
      demoPreLoaded: "Демо-файл предварительно загружен",
      photoRefs: "Референсные фото",
      photoRefsHint: "— необязательно, улучшает качество AI",
      dragOrClickPhotos: "Перетащите или нажмите для добавления референсных фото",
      upToPhotos: "До {max} фотографий",
      remove: "Удалить",
      inspirationNotes: "Заметки о вдохновении",
      inspirationPlaceholder: "Воспоминание, атмосфера, эмоциональный сигнал или конкретный импульс.",
    },
  },
  collections: {
    overline: "Студия дизайнера",
    title: "Коллекции",
    subtitle: "Группируйте ваши опубликованные дизайны в тщательно отобранные коллекции с AI-генерируемыми редакционными историями.",
    newCollection: "Новая коллекция",
    emptyTitle: "Ваша первая коллекция ждёт",
    emptySubtitle: "Выберите два или более опубликованных дизайна для создания коллекции. AI соткёт их истории воедино.",
    emptyHint: "или выберите существующую коллекцию на боковой панели",
    celebrationTitle: "Коллекция создана!",
    celebrationSubtitle: "Ваша коллекция готова к рабочему процессу истории. Начните с генерации ключевых слов ниже.",
    sidebar: { collections: "Коллекции", noCollections: "Коллекций пока нет" },
    designPicker: {
      title: "Изделия коллекции",
      subtitle: "Выберите опубликованные дизайны для включения. Необходимо не менее 2 дизайнов.",
      selected: "{count} выбрано",
      minRequired: "Выберите не менее 2 дизайнов",
      saveDesigns: "Сохранить изделия",
      saving: "Сохранение…",
    },
    create: {
      title: "Создать коллекцию",
      collectionTitle: "Название коллекции",
      titlePlaceholder: "например, Серия «Полуночный океан»",
      description: "Краткое описание",
      descriptionPlaceholder: "Несколько предложений о теме коллекции.",
      selectDesigns: "Выбрать дизайны",
      selectDesignsHint: "Выберите не менее 2 из ваших опубликованных дизайнов",
      inspirationNote: "Заметка о вдохновении",
      inspirationNotePlaceholder: "Что объединяет эти изделия?",
      submit: "Создать коллекцию",
      submitting: "Создание…",
    },
    draftNotInitialized: "Черновик коллекции не инициализирован.",
    draftNotInitializedCta: "Вернуться к коллекциям",
    workflow: {
      step00: "Изделия коллекции",
      step00Sub: "Управляйте тем, какие дизайны входят в эту коллекцию.",
      step01: "Ключевые слова коллекции",
      step01Sub: "AI анализирует все изделия вместе, чтобы найти объединяющие темы, мотивы и настроение.",
      additionalContext: "Дополнительный контекст",
      contextPlaceholder: "например, общий творческий сезон, палитра материалов, эмоциональная нить…",
      generateKeywords: "Сгенерировать ключевые слова",
      regenerateKeywords: "Перегенерировать ключевые слова",
      analysingCollection: "Анализ коллекции…",
      step02: "Выбор ключевых слов",
      step02Sub: "Выберите слова, которые лучше всего передают суть этой коллекции.",
      saveKeywords: "Сохранить выбранные ключевые слова",
      saving: "Сохранение…",
      step03: "Генерация истории коллекции",
      step03Sub: "AI соткёт индивидуальные истории дизайнов в цельный нарратив коллекции.",
      generateStory: "Сгенерировать историю",
      regenerateStory: "Перегенерировать историю",
      writingStory: "Написание истории коллекции…",
      step04: "Доработка истории",
      step04Sub: "Проверьте и отредактируйте сгенерированный нарратив коллекции.",
      saveStory: "Сохранить историю",
      step05: "Генерация витрины",
      step05Sub: "AI создаёт редакционную витрину из нескольких изделий с изображениями для каждого раздела.",
      currentLayout: "Текущий макет:",
      sections: "разделов",
      generatedImages: "сгенерированное изображение",
      generatedImagesPlural: "сгенерированных изображений",
      regenerateWarning: "Перегенерация заменяет текущий макет.",
      generateShowcase: "Сгенерировать витрину",
      regenerateShowcase: "Перегенерировать витрину",
      buildingShowcase: "Создание витрины…",
      step06: "Отправка на проверку",
      step06Sub: "Блокирует дальнейшее редактирование до тех пор, пока администратор не проверит историю и витрину вашей коллекции.",
      awaitingReview: "Ваша коллекция находится у команды проверки. Вы получите уведомление, когда будет принято решение.",
      submitForApproval: "Отправить на проверку",
      awaitingAdmin: "Ожидание проверки администратором…",
      published: "Опубликовано",
      submitting: "Отправка…",
      viewPublished: "Просмотреть опубликованную коллекцию",
      revisionRequired: "Требуется доработка",
      revisionHint: "Учтите отзывы выше, затем отправьте повторно.",
      noReason: "Причина не указана.",
      featured: "Рекомендуемый",
    },
  },
  inbox: {
    title: "Входящие запросы на кастомизацию",
    subtitle: "Согласовывайте условия, изучайте брифы и загружайте исправленные CAD файлы.",
    overline: "Студия дизайнера",
    openRequests: "Открытые запросы",
    emptyTitle: "Активных запросов нет",
    emptySubtitle: "Когда бренд отправит запрос на кастомизацию одного из ваших дизайнов, он появится здесь.",
    selectRequest: "Выберите запрос на боковой панели для начала.",
    thread: "Переписка",
    noMessages: "Сообщений пока нет.",
    updateStatus: "Обновить статус",
    uploadRevision: "Загрузить CAD ревизию",
    revisionHint: "Переведите запрос в статус «В работе», чтобы включить загрузку CAD ревизий.",
  },
  analytics: {
    overline: "Studio",
    title: "Аналитика дизайнов",
    subtitle: "Отслеживайте эффективность ваших дизайнов — просмотры, вовлечённость и охват аудитории.",
    cards: {
      totalViews: "Всего просмотров",
      uniqueVisitors: "Уникальных посетителей",
      avgTimeOnPage: "Среднее время на странице",
      quoteRequests: "Запросы коммерческих предложений",
    },
    table: {
      design: "Дизайн",
      views: "Просмотры",
      uniqueViews: "Уникальные",
      clicks: "Клики",
      avgTime: "Ср. время",
      scrollDepth: "Глубина прокрутки",
    },
    drillDown: {
      viewsTrend: "Динамика просмотров (30 дней)",
      clickBreakdown: "Разбивка кликов",
      referrerBreakdown: "Источники трафика",
    },
    empty: "Данных аналитики пока нет. Как только ваши дизайны будут опубликованы и просмотрены, здесь появятся показатели.",
    seconds: "с",
  },
},

  // ─── Catalog ──────────────────────────────────────────────────────────────
  catalog: {
    overline: "Торговый каталог",
    resultCount: "{count} результатов",
    designCount: "{count} дизайнов",
    emptyTitle: "Каталог формируется",
    emptySubtitle: "Дизайнеры готовят свои первые коллекции. Загляните позже.",
    searchEmptyTitle: "Совпадений не найдено",
    searchEmptySubtitle: "Попробуйте другой запрос или просмотрите все дизайны.",
    browseCta: "Просмотреть все дизайны",
  },

  // ─── Orders ───────────────────────────────────────────────────────────────
  orders: {
    overline: "Торговое рабочее пространство",
    title: "Закупочные заказы",
    subtitle: "Отслеживайте активные переговоры, просматривайте обновлённые 3D-прототипы от дизайнеров и утверждайте заявки для производственной маршрутизации.",
    activeOrders: "Активные заказы",
    emptyTitle: "Закупочных заказов пока нет",
    emptyCta: "Просмотреть торговый каталог →",
    selectOrder: "Выберите заказ для просмотра переговорной ветки.",
    latestRevision: "Последняя CAD-редакция — 3D-предпросмотр",
    awaitingRevision: "Ожидается первая CAD-редакция от дизайнера.",
    respondToDesigner: "Ответить дизайнеру",
  },

  // ─── Admin ─────────────────────────────────────────────────────────────────
  admin: {
    brand: "Администратор",
    nav: { workflows: "Рабочие процессы", publishing: "Публикация", users: "Пользователи", academy: "Академия", developer: "Разработчик", queues: "Монитор очередей", catalog: "Каталог", studio: "Студия", analytics: "Аналитика", catalogAdmin: "Каталог", catalogListings: "Торговые объявления", inspireAdmin: "Inspire", storyLab: "Мои дизайны" },
    users: {
      overline: "Администратор",
      title: "Управление пользователями",
      subtitle: "Создавайте, редактируйте и управляйте учётными записями пользователей всех ролей.",
      tabs: { users: "Пользователи", createNew: "Создать нового" },
      stats: { total: "Всего пользователей", active: "Активных", suspended: "Заблокированных", designers: "Дизайнеров" },
      search: "Поиск по имени или email…",
      allRoles: "Все роли",
      noMatch: "Ни один пользователь не соответствует вашим фильтрам.",
      columns: { name: "Имя", email: "Email", role: "Роль", status: "Статус", actions: "Действия" },
      statusActive: "Активен",
      statusSuspended: "Заблокирован",
      edit: "Редактировать",
      suspend: "Заблокировать",
      reactivate: "Реактивировать",
      roles: {
        DESIGNER: "Дизайнер",
        B2B_BUYER: "Бренд / Покупатель",
        MANUFACTURER: "Производитель",
        PRODUCTION_MANAGER: "Менеджер производства",
        ADMIN: "Администратор",
      },
      roleLabels: { DESIGNER: "Дизайнер", B2B_BUYER: "Бренд", MANUFACTURER: "Фабрика", PRODUCTION_MANAGER: "МП", ADMIN: "Администратор" },
      create: {
        title: "Создать учётную запись",
        subtitle: "Добавьте нового дизайнера, покупателя, производителя или сотрудника.",
        name: "Полное имя",
        namePlaceholder: "напр. Aria Atelier",
        email: "Адрес email",
        emailPlaceholder: "designer@example.com",
        password: "Пароль",
        passwordPlaceholder: "Мин. 8 символов",
        role: "Роль",
        submit: "Создать учётную запись",
      },
      invite: {
        title: "Отправить ссылку-приглашение",
        subtitle: "Создайте ссылку для регистрации для определённого email и роли. Действительна 7 дней.",
        email: "Email",
        emailPlaceholder: "new-designer@example.com",
        role: "Роль",
        submit: "Создать ссылку",
        copied: "Скопировано!",
        copy: "Копировать",
      },
      editDialog: {
        title: "Редактировать пользователя",
        subtitle: "Обновите данные учётной записи и назначение роли.",
        name: "Имя",
        email: "Email",
        role: "Роль",
        save: "Сохранить изменения",
        cancel: "Отмена",
        stripe: "Stripe",
        accountActions: "Действия с учётной записью",
        resetPassword: "Сбросить пароль",
        deleteAccount: "Удалить учётную запись",
      },
      confirm: {
        suspendTitle: "Заблокировать пользователя",
        suspendDesc: "Заблокировать {name}? Доступ к платформе будет немедленно отозван.",
        suspendButton: "Заблокировать",
        reactivateTitle: "Реактивировать пользователя",
        reactivateDesc: "Реактивировать {name}? Доступ будет полностью восстановлен.",
        reactivateButton: "Реактивировать",
        deleteTitle: "Удалить пользователя",
        deleteDesc: "Удалить {name} навсегда? Это действие нельзя отменить, и все связанные данные будут удалены.",
        deleteButton: "Удалить навсегда",
        cancel: "Отмена",
      },
      success: {
        created: "Учётная запись пользователя успешно создана.",
        updated: "Данные пользователя обновлены.",
        suspended: "Учётная запись пользователя заблокирована.",
        reactivated: "Учётная запись пользователя реактивирована.",
        deleted: "Учётная запись пользователя удалена.",
        reset: "Временный пароль создан.",
        invited: "Ссылка-приглашение создана.",
      },
      tempPasswordHint: "Скопируйте сейчас — это не будет показано снова.",
      dismiss: "Закрыть",
    },
    setup: {
      title: "Настройка платформы",
      subtitle: "Создайте первую учётную запись администратора для начала работы.",
      steps: { authorize: "Авторизация", credentials: "Учётные данные", designer: "Дизайнер", confirm: "Подтверждение" },
      setupPasswordLabel: "Пароль установки",
      setupPasswordPlaceholder: "Введите пароль установки",
      setupPasswordHint: "Для создания первой учётной записи администратора требуется пароль установки.",
      setupPasswordInvalid: "Неверный пароль установки.",
      emailLabel: "Адрес email",
      emailPlaceholder: "admin@company.com",
      nameLabel: "Полное имя",
      namePlaceholder: "Иван Иванов",
      passwordLabel: "Пароль",
      passwordPlaceholder: "Минимум 8 символов",
      confirmPasswordLabel: "Подтвердите пароль",
      confirmPasswordPlaceholder: "Повторите пароль",
      designerTitle: "Учётная запись дизайнера",
      designerSubtitle: "Создайте первую учётную запись дизайнера, чтобы они могли начать загружать дизайны.",
      designerNameLabel: "Имя дизайнера",
      designerNamePlaceholder: "Aria Atelier",
      designerEmailLabel: "Email дизайнера",
      designerEmailPlaceholder: "designer@company.com",
      designerPasswordLabel: "Пароль дизайнера",
      designerPasswordPlaceholder: "Минимум 8 символов",
      reviewTitle: "Проверка",
      reviewSubtitle: "Подтвердите учётные записи, которые будут созданы.",
      createAccount: "Создать учётные записи",
      back: "Назад",
      next: "Далее",
      passwordMismatch: "Пароли не совпадают.",
      passwordTooShort: "Пароль должен содержать не менее 8 символов.",
    },
    analytics: {
      overline: "Администратор",
      title: "Аналитика платформы",
      subtitle: "Метрики вовлечённости с дизайнами по всей платформе и показатели производительности.",
      cards: {
        totalViews: "Всего просмотров",
        uniqueVisitors: "Уникальных посетителей",
        avgTimeOnPage: "Среднее время на странице",
        totalClicks: "Всего кликов",
      },
      filters: {
        designer: "Фильтр по дизайнеру",
        dateRange: "Диапазон дат",
        allDesigners: "Все дизайнеры",
      },
      leaderboard: "Лучшие дизайны",
      zeroEngagement: "Требует внимания (нет вовлечённости)",
      table: {
        design: "Дизайн",
        designer: "Дизайнер",
        views: "Просмотры",
        uniqueViews: "Уникальные",
        clicks: "Клики",
        avgTime: "Среднее время",
        scrollDepth: "Глубина прокрутки",
      },
      drillDown: {
        viewsTrend: "Динамика просмотров",
        clickBreakdown: "Разбивка кликов",
        referrerBreakdown: "Источники переходов",
      },
      empty: "Данных аналитики пока нет. После публикации и просмотра дизайнов здесь появятся метрики.",
      seconds: "с",
    },
  },

  // ─── Chat / Messages ────────────────────────────────────────────────────────
  messages: {
    overline: "Коммуникация",
    title: "Сообщения",
    subtitle: "Переписка с дизайнерами, менеджерами производства и производственными партнёрами.",
    emptyTitle: "Переписок пока нет",
    emptyCta: "Просмотрите каталог, чтобы начать",
    typeMessage: "Введите сообщение...",
    send: "Отправить",
    translate: "Перевести",
    translating: "Перевод...",
    loadMore: "Загрузить более ранние сообщения",
    typing: "печатает...",
    typingMultiple: "печатают...",
    today: "Сегодня",
    yesterday: "Вчера",
    unread: "непрочитанных",
    newConversation: "Новый разговор",
    participants: "Участники",
    quoteProposal: "Предложение о цене",
    statusUpdate: "Обновление статуса",
    attachFile: "Прикрепить файл",
    accept: "Принять",
    counter: "Встречное предложение",
    connecting: "Подключение к чату...",
    estimatedDays: "Предполагаемая доставка",
    days: "дней",
    messageDesigner: "Написать дизайнеру",
  },

  // ─── Academy ──────────────────────────────────────────────────────────────
  academy: {
    meta: {
      title: "Академия — Caratzon",
      description: "Экспертные руководства по производству ювелирных изделий, построению бренда и отраслевым тенденциям для дизайнеров и микробрендов.",
    },
    hero: {
      title: "Академия Caratzon",
      subtitle: "Экспертные знания для ювелирных дизайнеров и новых брендов — от CAD до коммерции.",
    },
    categories: {
      all: "Все",
      manufacturing: "Производство",
      business: "Бизнес",
      trends: "Тенденции",
      wholesale: "Оптовая торговля",
      brand: "Бренд",
      tech: "Технологии",
      operations: "Операции",
    },
    readingTime: "мин чтения",
    featured: "Избранное",
    relatedArticles: "Похожие статьи",
    backToAcademy: "← Назад в Академию",
    cta: {
      title: "Готовы воплотить свои дизайны в жизнь?",
      description: "Caratzon соединяет независимых дизайнеров с сертифицированными производителями для бесперебойного производства.",
      button: "Начать на Caratzon →",
    },
    rss: "RSS-лента",
    noArticles: "Статей пока нет. Загляните позже!",
  },

  // ─── Newsroom ─────────────────────────────────────────────────────────────
  newsroom: {
    meta: {
      title: "Пресс-центр — Caratzon",
      description:
        "Последние новости, пресс-релизы и обновления от Caratzon. Объявления компании, запуски продуктов, партнёрства и отраслевые аналитические материалы.",
    },
    hero: {
      issueLabel: "Пресс-центр",
      title: "Последнее от Caratzon",
      subtitle: "Новости компании, пресс-релизы, обновления продуктов и отраслевые аналитические материалы.",
    },
    categories: {
      all: "Все",
      company: "Компания",
      product: "Продукт",
      partnership: "Партнёрство",
      industry: "Отрасль",
      press_release: "Пресс-релиз",
    },
    readingTime: "мин чтения",
    featured: "Избранное",
    relatedArticles: "Похожие статьи",
    backToNewsroom: "← Назад в пресс-центр",
    cta: {
      title: "Готовы воплотить свои дизайны в жизнь?",
      description:
        "Caratzon соединяет независимых дизайнеров с сертифицированными производителями для бесперебойного производства.",
      button: "Начать на Caratzon →",
    },
    noArticles: "Статей пока нет. Загляните позже!",
    pressAssets: {
      meta: {
        title: "Пресс-материалы — Пресс-центр Caratzon",
        description:
          "Логотипы бренда, руководства и медиаресурсы для прессы и партнёров. Скачайте фирменные материалы Caratzon для редакционного использования.",
      },
      hero: {
        issueLabel: "Пресс-материалы",
        title: "Пресс-материалы",
        subtitle:
          "Фирменные ресурсы для прессы, партнёров и редакционного использования. Скачайте логотипы и руководства ниже.",
      },
      logos: {
        heading: "Логотипы бренда",
        primary: "Основной логотип (светлый)",
        dark: "Основной логотип (тёмный)",
        icon: "Только иконка",
        stacked: "Вертикальный",
      },
      guidelines: {
        heading: "Руководство по фирменному стилю",
        colorPalette: "Цветовая палитра",
        colorNotes:
          "Основной фирменный акцент: Teal Sapphire (#005F6B). Тёмные чернила Индии для текста. Тёплый кремовый велюм для фонов. Золотая фольга — только для декоративного использования.",
        typography: "Типографика",
        typographyNotes:
          "Заголовки: Cormorant Garamond, Cormorant SC. Основной текст: Inter. Используйте шрифты заголовков для заглавий и редакционных акцентов; Inter — для интерфейса и основного текста.",
      },
      contact: {
        heading: "Контакт для прессы",
        description: "По вопросам СМИ, пресс-кит и запросам на интервью:",
        contactLink: "Общие вопросы →",
      },
    },
  },

  contentKit: {
    nav: "Caratzon Kits",
    meta: {
      title: "Caratzon Kits — Caratzon",
      description: "Создавайте полные Caratzon Kits для ваших ювелирных дизайнов — фотографии на моделях, съёмки для лукбука, изображения для каталога, подписи для соцсетей и редакционные истории.",
    },
    list: {
      title: "Ваши Caratzon Kits",
      subtitle: "Создавайте готовые к маркетингу фотографии на моделях, визуальные материалы, тексты и лукбуки из ваших дизайнов.",
      createNew: "Новый Caratzon Kit",
      empty: "Caratzon Kits пока нет. Создайте первый, чтобы начать.",
      status: {
        draft: "Черновик",
        generating: "Генерация",
        ready: "Готово",
        failed: "Ошибка",
      },
    },
    wizard: {
      step1: "Выбор источника",
      step2: "Генерация визуалов",
      step3: "Генерация текста",
      step4: "Создание лукбука",
      step5: "Публикация и распространение",
      source: {
        title: "Выберите источник дизайна",
        fromCatalog: "Из каталога",
        fromCatalogDesc: "Выберите дизайн, лицензированный вами на маркетплейсе.",
        uploadNew: "Загрузить изображения",
        uploadNewDesc: "Загрузите собственные фотографии ювелирных изделий.",
        titleLabel: "Название изделия",
        descriptionLabel: "Описание",
        next: "Генерировать визуалы",
      },
      visuals: {
        title: "Ваши сгенерированные визуалы",
        subtitle: "Улучшенные с помощью AI снимки товара и образы в стиле лайфстайл.",
        generating: "Генерация визуалов...",
        regenerate: "Перегенерировать",
        acceptAll: "Принять и продолжить",
        catalogShot: "Снимок товара",
        lookbookShot: "Лайфстайл",
      },
      copy: {
        title: "Маркетинговые тексты",
        subtitle: "Описание товара и подписи для соцсетей, сгенерированные AI.",
        generating: "Написание текстов...",
        productDescription: "Описание товара",
        brandStory: "История бренда",
        socialCaptions: "Подписи для соцсетей",
        regenerate: "Перегенерировать тексты",
        next: "Создать лукбук",
      },
      lookbook: {
        title: "Предпросмотр лукбука",
        subtitle: "Ваш лукбук, собранный AI и готовый к загрузке.",
        generating: "Сборка лукбука...",
        template: "Стиль шаблона",
        regenerate: "Перегенерировать в другом стиле",
        templates: {
          minimal: "Минимализм",
          editorial: "Редакционный",
          bold: "Яркий",
          organic: "Органика",
        },
        next: "Публикация и распространение",
      },
      publish: {
        title: "Публикация и распространение",
        subtitle: "Скачайте ваш Caratzon Kit или опубликуйте его в Discovery.",
        download: "Скачать",
        downloadAll: "Скачать полный Kit",
        downloadImages: "Скачать изображения (ZIP)",
        downloadCopy: "Скачать тексты (TXT)",
        downloadPdf: "Скачать лукбук (PDF)",
        publish: "Опубликовать в Discovery",
        publishDesc: "Сделайте вашу страницу discovery публичной на Caratzon.",
        slugLabel: "URL Discovery",
        categoryLabel: "Категория",
        materialsLabel: "Материалы",
        keywordsLabel: "Стилевые ключевые слова",
        heroLabel: "Главное изображение",
        published: "Опубликовано!",
        viewDiscovery: "Просмотреть страницу Discovery",
      },
    },
    upsell: {
      title: "У вас закончились студийные кредиты",
      body: "Перейдите на Source и получайте 1 500 студийных кредитов каждый месяц — достаточно для 15 Caratzon Kits. Или мгновенно пополните пакетом кредитов.",
      cta: "Просмотреть планы",
    },
  },

  concierge: {
    title: "Консьерж Caratzon",
    subtitle: "Ваш B2B-ассистент по закупке ювелирных изделий",
    welcome: "Добро пожаловать! Задайте вопрос о наших дизайнах, лицензионных уровнях, производстве или о чём угодно, связанном с Caratzon.",
    inputPlaceholder: "Спросите о дизайнах, лицензировании, ценах...",
    disclaimer: "На основе AI. Ответы могут быть неточными.",
    quickActions: [
      "Как работает лицензирование?",
      "Я дизайнер — как присоединиться?",
      "Какие тарифные планы?",
      "Я хотел бы поговорить с кем-то",
    ],
    leadForm: {
      title: "Свяжитесь с нашей командой",
      description: "Поделитесь своими данными, и наша команда свяжется с вами в течение 24 часов.",
      name: "Ваше имя",
      email: "Рабочий email",
      company: "Компания (необязательно)",
      message: "Кратко опишите, с чем вам нужна помощь...",
      submit: "Связать меня с командой",
      successTitle: "Вы подключены",
      success: "Наша команда свяжется с вами в течение 24 часов. Пока вы можете продолжить общение здесь.",
    },
    handoffConfirm: {
      title: "Запрос на передачу",
      description: "Мы уведомили нашу команду с деталями вашего запроса. Сотрудник свяжется с вами в ближайшее время. Вы можете продолжить общение здесь или обратиться к нам напрямую по адресу trade@caratzon.com.",
    },
  },

  inspire: {
    meta: {
      title: "Inspire — Caratzon",
      description: "Исследуйте потрясающие ювелирные дизайны от дизайнеров и брендов со всего мира.",
    },
    explore: {
      title: "Inspire",
      subtitle: "Исследуйте ювелирные дизайны от создателей со всего мира.",
      allCategories: "Все",
      noResults: "В этой категории изделий не найдено.",
    },
    profile: {
      pieces: "Изделия",
      totalViews: "Всего просмотров",
    },
    detail: {
      by: "от",
      views: "просмотров",
      materials: "Материалы",
      share: "Поделиться",
      viewProfile: "Просмотреть профиль",
      cta: {
        title: "Создайте свою страницу Inspire",
        body: "Присоединитесь как дизайнер, чтобы демонстрировать свои работы с помощью лукбуков, созданных AI, редакционных историй и персонального профиля — всё из вашей Студии.",
        button: "Начать создавать",
      },
    },
    saved: {
      title: "Сохранённые изделия",
      empty: "Сохранённых изделий пока нет.",
      emptySubtitle: "Просматривайте Inspire, чтобы найти избранное.",
      backToInspire: "Назад в Inspire",
    },
    creatorOnboarding: {
      meta: {
        title: "Присоединиться к Inspire как создатель · Caratzon",
        description: "Расскажите нам о своём мастерстве, вашей истории и вашем видении — чтобы мы могли представить вас миру.",
      },
      header: "Inspire · Регистрация создателя",
      stepOf: "Шаг {step} из {total}",
      back: "← Назад",
      continue: "Продолжить →",
      steps: {
        profile: "Профиль",
        styleTools: "Стиль и инструменты",
        creativeDna: "Творческое ДНК",
        philosophy: "Философия",
        innerWorld: "Внутренний мир",
        production: "Производство",
        review: "Проверка",
      },
      profile: {
        title: "Ваш профиль дизайнера",
        subtitle: "Так вас будут видеть покупатели и менеджеры производства на платформе.",
        firstName: "Имя *",
        lastName: "Фамилия *",
        studioName: "Название студии / бренда",
        studioPlaceholder: "напр. Studio Noir, Aria Atelier",
        location: "Местоположение / Город",
        locationPlaceholder: "напр. Париж, Франция",
        photo: "Фото профиля",
        photoOptional: "(необязательно — улучшается AI)",
        photoHint: "Загрузите деловой портрет или неформальное фото. Наш AI улучшит его, добавив тёплое студийное освещение и чистый фон.",
        photoEnhancing: "Улучшение фото с помощью AI…",
        photoSaved: "Фото улучшено и сохранено ✓",
        photoChange: "Изменить",
        photoUpload: "Загрузить",
      },
      style: {
        title: "Ваша эстетика и инструменты",
        subtitle: "Мы используем эти данные для подбора вашей аудитории покупателей.",
        aestheticLabel: "Как бы вы описали свою дизайнерскую эстетику? *",
        aestheticHint: "Выберите все подходящие варианты",
        designStyleLabels: {
          "Minimalist": "Минимализм",
          "Vintage-inspired": "В винтажном стиле",
          "Bridal/Engagement": "Свадебный / Помолвочный",
          "Men's": "Мужской",
          "Bold/Chunky": "Смелый / Массивный",
          "Trend-driven Fashion": "Трендовая мода",
          "Everyday Fine": "Повседневная элегантность",
          "Maximalist": "Максимализм",
        } as Record<string, string>,
        cadLabel: "Какое CAD-программное обеспечение вы используете преимущественно? *",
        cadHint: "Выберите все подходящие варианты",
        formatsLabel: "Какие форматы файлов вы можете предоставить?",
        formatsHint: "Выберите все подходящие варианты",
      },
      creative: {
        title: "Ваше творческое ДНК",
        subtitle: "Помогите нам понять корни вашего мастерства. Эти ответы формируют то, как AI рассказывает вашу историю покупателям.",
        originLabel: "Как вы стали ювелирным дизайнером? *",
        originHint: "Ваша история происхождения — что привлекло вас к этому ремеслу? Воспоминание из детства, наставник, поворотный момент?",
        originPlaceholder: "Я выросла, наблюдая, как моя бабушка нанизывает жемчуг на шёлк в своей мастерской в Джайпуре. Свет, отражавшийся от каждой бусины, казался мне волшебством…",
        materialsLabel: "С какими материалами вы чувствуете наибольшую связь?",
        materialsHint: "Выберите всё, что вас привлекает",
        signatureMaterialLabels: {
          "Gold (yellow, rose, white)": "Золото (жёлтое, розовое, белое)",
          "Platinum": "Платина",
          "Silver / Sterling": "Серебро / Стерлинговое",
          "Titanium": "Титан",
          "Enamel": "Эмаль",
          "Precious gemstones": "Драгоценные камни",
          "Semi-precious stones": "Полудрагоценные камни",
          "Pearls": "Жемчуг",
          "Diamonds": "Бриллианты",
          "Wood / organic materials": "Дерево / органические материалы",
          "Recycled metals": "Переработанные металлы",
          "Lab-grown stones": "Выращенные в лаборатории камни",
          "Mixed media": "Смешанные материалы",
        } as Record<string, string>,
        ritualsLabel: "Опишите ваш творческий ритуал",
        ritualsHint: "Как выглядит ваш процесс? Вы сначала делаете наброски, сразу переходите к CAD, работаете с воском? Где вы работаете?",
        ritualsPlaceholder: "Я всегда начинаю с акварельных эскизов на фактурной бумаге. Мне нужно почувствовать вес и течение, прежде чем прикоснуться к экрану…",
        culturalLabel: "Какие культурные или исторические влияния формируют вашу работу?",
        culturalHint: "Традиции, регионы, эпохи, художественные движения — всё, что питает ваш визуальный язык.",
        culturalPlaceholder: "Геометрия ар-деко, флористические мотивы эпохи Великих Моголов, японское несовершенство ваби-саби…",
      },
      philosophy: {
        title: "Ваша дизайнерская философия",
        subtitle: "Эти вопросы раскрывают, как вы думаете о своём ремесле — противоречия, убеждения и выборы, которые делают вашу работу уникальной.",
        oneWordLabel: "Если бы вы могли описать свою дизайнерскую философию одним словом, что бы это было? *",
        oneWordPlaceholder: "напр. Напряжение, Почтение, Вызов, Текучесть",
        feelingLabel: "Когда кто-то носит ваше украшение, что они должны чувствовать? *",
        feelingHint: "Не как они должны выглядеть — какое эмоциональное состояние должно вызывать изделие?",
        feelingPlaceholder: "Как будто они хранят секрет — что-то драгоценное и только своё. Тихая уверенность, которой не нужно заявлять о себе.",
        tensionLabel: "Какое творческое противоречие определяет вашу работу?",
        tensionHint: "Каждый дизайнер балансирует между компромиссами. Орнамент или сдержанность? Традиция или разрушение? Совершенство или необработанность?",
        tensionPlaceholder: "Меня всегда тянет между точностью и несовершенством. Я хочу, чтобы каждая линия была намеренной, но оставляю место для сюрпризов металла…",
        opinionLabel: "Какое непопулярное мнение о ювелирном дизайне вы разделяете?",
        opinionHint: "Что-то, во что вы верите, с чем большинство в отрасли не согласится.",
        opinionPlaceholder: "Я считаю, что симметрия переоценена. Самые красивые изделия имеют намеренную асимметрию, которая делает их живыми…",
      },
      innerWorld: {
        title: "Ваш внутренний мир",
        subtitle: "Эти более глубокие вопросы помогают нашему AI понять, кто вы за пределами мастерской — чтобы ваша история дизайнера была подлинно вашей.",
        inspirationLabel: "Что за пределами ювелирного дела вдохновляет ваши дизайны? *",
        inspirationHint: "Архитектура, музыка, природа, еда, кино, математика, город, ощущение — что угодно.",
        inspirationPlaceholder: "То, как брутальный бетон ловит дневной свет. Математические спирали в раковинах наутилуса. Тишина японского сада камней…",
        emotionalLabel: "Какую эмоциональную потребность удовлетворяет дизайн для вас?",
        emotionalHint: "Почему вы продолжаете создавать? Чего бы не хватало в вашей жизни без этого?",
        emotionalPlaceholder: "Это единственное время, когда мой разум успокаивается. Когда я глубоко погружена в дизайн, шум утихает, и остаёмся только я и изделие…",
        legacyLabel: "Что вы хотите, чтобы ваши работы говорили о вас через 20 лет?",
        legacyHint: "Ваше видение наследия — какая нить должна связывать всё, что вы создаёте?",
        legacyPlaceholder: "Что я создавала изделия, которые люди не просто носили, но хранили. Предметы, ставшие частью чьей-то истории, передаваемые по наследству не за их ценность, а за их смысл…",
        metaphorLabel: "Если бы ваша творческая практика была местом, как бы оно выглядело?",
        metaphorHint: "Метафора для вашего творческого пространства — реального или воображаемого.",
        metaphorPlaceholder: "Стеклянная мастерская на краю утёса. Точные инструменты, дикий вид. Внутри всё упорядочено; снаружи всё неприручено…",
      },
      production: {
        title: "Готовность к производству",
        subtitle: "Производителям нужны CAD-файлы, оптимизированные для физического производства — толщина крапанов, вес металла, допуски усадки.",
        cadOptimisedLabel: "Ваши CAD-файлы полностью оптимизированы для физического производства?",
        optionYes: "Да — мои файлы полностью оптимизированы для производства",
        optionMostly: "В основном — могут потребоваться незначительные корректировки",
        optionNo: "Нет — я проектирую только для рендеринга/визуализации",
        optionHelp: "Мне нужна помощь с оптимизацией файлов",
        portfolioLabel: "Портфолио / Сайт",
        portfolioPlaceholder: "https://yourportfolio.com",
        socialLabel: "Социальные профили",
        socialOptional: "(необязательно — отображается на вашей странице Inspire)",
        instagramPlaceholder: "@yourstudio или https://instagram.com/yourstudio",
        pinterestPlaceholder: "@yourstudio или https://pinterest.com/yourstudio",
        tiktokPlaceholder: "@yourstudio или https://tiktok.com/@yourstudio",
        sampleUploadsTitle: "Примеры загрузок",
        sampleUploadsBody: "Вы сможете загружать CAD-рендеры и фотографии готовых изделий непосредственно из панели управления Студии после завершения профиля.",
      },
      review: {
        title: "Проверьте свой профиль",
        subtitle: "Всё выглядит правильно? После одобрения вы сможете обновить эти данные из настроек Студии.",
        aiEnhanced: "Портрет, улучшенный AI",
        labels: {
          name: "Имя",
          studio: "Студия",
          location: "Местоположение",
          aesthetic: "Эстетика",
          cadSoftware: "CAD-программы",
          fileFormats: "Форматы файлов",
          productionReady: "Готовность к производству",
          portfolio: "Портфолио",
          instagram: "Instagram",
          pinterest: "Pinterest",
          tiktok: "TikTok",
        },
        creativeProfile: "Ваш творческий профиль",
        creativeLabels: {
          originStory: "История происхождения",
          materials: "Материалы",
          philosophy: "Философия",
          wearerFeels: "Ощущение носителя",
          inspiration: "Вдохновение",
          legacy: "Наследие",
        },
        storyGeneration: {
          title: "Создайте вашу историю дизайнера",
          subtitle: "Наш AI создаст убедительный нарратив из ваших ответов на анкету — готовый к публикации на вашей странице Inspire.",
          button: "Создать мою историю",
          loading: "Создание вашего нарратива…",
          error: "Создание истории не удалось",
          tryAgain: "Попробовать снова",
          refineLater: "После одобрения вы сможете доработать эту историю из панели управления Студии.",
        },
        terms: {
          prefix: "Я подтверждаю, что приведённая информация верна, и соглашаюсь с",
          termsLink: "условиями обслуживания дизайнеров",
          and: "и",
          privacyLink: "политикой конфиденциальности",
        },
        submit: "Отправить заявку →",
        submitting: "Отправка…",
      },
      errors: {
        nameRequired: "Пожалуйста, введите ваше имя и фамилию.",
        agreeRequired: "Вы должны согласиться с условиями для дизайнеров, чтобы продолжить.",
        submissionFailed: "При отправке вашей заявки возникла ошибка. Пожалуйста, попробуйте снова.",
      },
      pending: {
        meta: {
          title: "Заявка на рассмотрении · Caratzon",
          description: "Ваша заявка дизайнера рассматривается нашей командой.",
        },
        overline: "Закрытая бета",
        thankYou: "Спасибо, {name}",
        thankYouGeneric: "Спасибо",
        submitted: "Ваша заявка успешно отправлена. Caratzon сейчас находится в",
        privateBeta: "закрытой бете",
        submittedSuffix: ", и наша команда лично рассматривает каждую заявку дизайнера.",
        emailNotice: "Мы уведомим вас по email, как только ваша учётная запись будет одобрена. Обычно это занимает 1–2 рабочих дня.",
        backToHome: "На главную",
        exploreAcademy: "Исследовать Академию →",
        rejected: {
          overline: "Обновление заявки",
          heading: "Мы не смогли одобрить вашу заявку в данный момент",
          body: "Спасибо за интерес к Caratzon. К сожалению, мы не можем одобрить вашу заявку прямо сейчас. Если вы считаете, что это ошибка, или хотите предоставить дополнительную информацию, пожалуйста, свяжитесь с нашей командой.",
          feedbackLabel: "Отзыв от нашей команды",
          contactUs: "Связаться с нами",
          reapply: "Подать заявку снова",
          reapplying: "Отправка…",
        },
      },
    },
  },

  caraChat: {
    empty: {
      title: "Познакомьтесь с Cara",
      description: "Ваш AI-ассистент по дизайну — мастер-ювелир и инженер-конструктор в одном лице. Опишите своё видение, загрузите эскиз или добавьте вдохновляющее изображение, чтобы начать.",
      sketchToConcept: "Эскиз → Концепция",
      photoToDesign: "Фото → Дизайн",
      ideaToRender: "Идея → Рендер",
    },
    placeholder: {
      initial: "Опишите своё видение, загрузите эскиз или добавьте изображение для вдохновения…",
      direction: "Укажите Cara направление — материалы, стиль, эпоха…",
      adjust: "Попросите Cara скорректировать дизайн или запросите структурный анализ…",
      finalize: "Готовы завершить? Попросите Cara создать каталожный рендер и спецификацию материалов.",
    },
    dragOverlay: "Перетащите эскиз или референсное изображение",
    scrollPill: "↓ Новые сообщения",
    sketchFallback: "Вот мой эскиз / референсное изображение. Пожалуйста, проанализируйте его.",
    error: "Что-то пошло не так. Пожалуйста, попробуйте снова.",
    regenerate: "Перегенерировать",
    suggestions: {
      custom: "Другое…",
      customPlaceholder: "Введите ваш ответ…",
      send: "Отправить",
    },
    editMessage: "Редактировать сообщение",
    editSend: "Отправить",
    editCancel: "Отмена",
    download: "Скачать",
    copyMessage: "Копировать сообщение",
    justNow: "Только что",
    minutesAgo: "{n}м назад",
    toolLabels: {
      generate_mood_board: "Мудборд",
      generate_concept_sketch: "Концептуальный эскиз",
      generate_design_render: "Рендер дизайна",
      adjust_design: "Скорректированный рендер",
      generate_catalog_render: "Каталожный рендер",
      render_from_sketch: "Концепция из эскиза",
      analyze_sketch: "Анализ эскиза",
      analyze_inspiration: "Анализ вдохновения",
      reference_lookup: "Поиск референсов",
      structural_analysis: "Структурный анализ",
      calculate_bom: "Спецификация материалов",
      material_recommendation: "Анализ материалов",
      generate_turntable_video: "Видео поворота",
      generate_lookbook_image: "Изображение для лукбука",
      present_design_specs: "Характеристики дизайна",
      finalize_design: "Завершённый дизайн",
      share_to_gallery: "Поделиться в галерее",
    },
    toolLoading: {
      rendering: "Рендеринг…",
      sketchingConcept: "Создание концепции…",
      analyzingSketch: "Анализ вашего эскиза…",
      analyzingInspiration: "Анализ вдохновения…",
      lookingUpReference: "Поиск референсных данных…",
      generatingVideo: "Генерация видео — это может занять до минуты…",
      stylingLookbook: "Оформление съёмки для лукбука…",
      structuralAnalysis: "Выполнение симуляции производимости…",
      materialCheck: "Проверка совместимости материалов…",
      calculatingBom: "Расчёт спецификации материалов…",
      sharingToGallery: "Публикация в галерее…",
      preparingSpecs: "Подготовка характеристик…",
      finalizingDesign: "Завершение дизайна…",
      processing: "Обработка…",
    },
    toolCards: {
      moodBoard: "Мудборд",
      conceptSketch: "Концептуальный эскиз",
      refinedSketch: "Доработанный эскиз",
      turntablePreview: "Предпросмотр поворота",
      sketchAnalysis: "Анализ эскиза",
      inspirationAnalysis: "Анализ вдохновения",
      structuralAnalysis: "Структурный анализ",
      billOfMaterials: "Спецификация материалов",
      materialAnalysis: "Анализ материалов",
      designFinalized: "Дизайн завершён",
      sharedToGallery: "Опубликовано в галерее",
      errorFallback: "Что-то пошло не так.",
      designRender: "Рендер дизайна",
      updatedRender: "Обновлённый рендер",
      catalogRender: "Каталожный рендер",
      conceptFromSketch: "Концепция из эскиза",
      lookbookLabel: "Лукбук",
      saveToStudio: "Сохранить в Студию",
      savedOpenStudio: "Сохранено — Открыть в Студии",
      submitBrief: "Отправить дизайн-бриф",
      briefSubmitted: "Бриф отправлен — Просмотреть заказы",
      designTitlePlaceholder: "Название дизайна…",
      saving: "Сохранение…",
      save: "Сохранить",
      cancel: "Отмена",
      structural: {
        manufacturabilityReport: "Отчёт о производимости",
        areasAnalyzed: "{n} область проанализирована",
        areasAnalyzedPlural: "{n} областей проанализировано",
        issuesFound: "ОБНАРУЖЕНЫ ПРОБЛЕМЫ",
        warnings: "ПРЕДУПРЕЖДЕНИЯ",
        allClear: "ВСЁ В ПОРЯДКЕ",
        pass: "пройдено",
        warning: "предупреждение",
        warningPlural: "предупреждений",
        critical: "критическое",
        recommendationLabel: "Рекомендация:",
        addressCritical: "Устраните критические проблемы перед переходом к литью.",
        reviewWarnings: "Проверьте предупреждения выше — дизайн реализуем с указанными корректировками.",
      },
      bom: {
        costBreakdown: "Разбивка стоимости",
        lineItems: "{n} позиция",
        lineItemsPlural: "{n} позиций",
        item: "Позиция",
        qty: "Кол-во",
        estCost: "Прим. стоимость",
        laborSetting: "Труд и закрепка",
        totalEstimate: "Итого (приблизительно)",
      },
      material: {
        compatibilityCheck: "Проверка совместимости",
        compatible: "СОВМЕСТИМО",
        concerns: "ЗАМЕЧАНИЯ",
        concernsLabel: "Замечания",
        recommendationsLabel: "Рекомендации",
        allCompatible: "Все материалы совместимы для производства.",
      },
      sketch: {
        sketchInterpretation: "Интерпретация эскиза",
        needsInput: "НУЖЕН ВВОД",
        stones: "Камни",
        filigree: "Филигрань",
        engraving: "Гравировка",
        designNotes: "Заметки по дизайну",
        needsClarification: "Требует уточнения",
      },
      inspiration: {
        inspirationBreakdown: "Разбор вдохновения",
        colorPalette: "Цветовая палитра",
        motifs: "Мотивы",
        textures: "Текстуры",
        suggestedFinishes: "Предлагаемые отделки",
        suggestedForms: "Предлагаемые формы",
      },
      designSpecs: {
        weight: "Вес",
        stones: "Камни",
        stoneSingular: "Камень",
        finish: "Отделка",
        stonesLabel: "Камни",
        dimensions: "Размеры",
        manufacturable: "Производимо",
        manufacturabilityConcerns: "Замечания по производимости",
      },
      finalize: {
        productionReady: "Готово к производству",
        metal: "Металл",
        stones: "Камни",
        stoneSingular: "Камень",
        estRetail: "Прим. розничная цена",
        cadHandoff: "Готово к передаче CAD, литью или запросу коммерческого предложения",
      },
      gallery: {
        nowInGallery: "Теперь в галерее",
        storyGenerating: "Создаётся история дизайна — проверьте вкладку Галерея, чтобы её увидеть.",
      },
      reference: {
        title: "Референсные данные",
        casting: "Характеристики литья",
        metals: "Свойства металлов",
        stones: "Долговечность камней",
        settings: "Совместимость закрепок",
      },
    },
    starters: [
      { label: "Дизайн кольца-солитер", text: "Спроектируйте современное помолвочное кольцо-солитер с овальным бриллиантом 1 карат из розового золота 18 карат" },
      { label: "Коктейльное кольцо ар-деко", text: "Создайте смелое коктейльное кольцо, вдохновлённое геометрией ар-деко с изумрудами и ониксом" },
      { label: "Минималистский теннисный браслет", text: "Спроектируйте минималистский теннисный браслет с чередующимися сапфирами и бриллиантами в платине" },
    ],
    buyerOverrides: {
      empty: {
        title: "Создавайте дизайны с Cara",
        description: "Опишите украшение, которое вы ищете — стиль, материалы, повод — и Cara воплотит ваше видение в рендерах, готовых к производству.",
      },
      placeholder: {
        initial: "Опишите украшение, которое вы ищете — стиль, материалы, повод…",
        direction: "Расскажите Cara подробнее о ваших предпочтениях — материалы, бюджет, количество…",
        adjust: "Попросите Cara скорректировать дизайн или изучить различные варианты…",
        finalize: "Вас устраивает дизайн? Отправьте его как дизайн-бриф для производства.",
      },
      starters: [
        { label: "Индивидуальное помолвочное кольцо", text: "Мне нужно индивидуальное помолвочное кольцо — овальный бриллиант, современная оправа, розовое золото" },
        { label: "Изделие для брендовой коллекции", text: "Мне нужен подвес-визитная карточка для моего бренда — минималистский, золото 14 карат, розничная цена до $200" },
        { label: "Набор обручальных колец", text: "Спроектируйте парный набор обручальных колец в платине с тонкими деталями" },
      ],
    },
  },

  // ─── Pricing page (/brands/pricing) ───────────────────────────────────────
  pricing: {
    meta: {
      title: "Тарифы · Caratzon",
      description: "Прозрачные тарифы для брендов на Caratzon — источайте предварительно проработанные дизайны украшений, генерируйте AI-маркетинговые наборы и производите устойчиво.",
    },
    hero: {
      overline: "Для брендов · Торговое членство",
      heading: "Перестаньте платить $2 500 за дизайн.",
      headingEm: "Начните с $0.",
      body: "Ваше членство открывает доступ ко всему каталогу, AI-инструментам маркетинга и прямому sourcing'у от независимых дизайнеров. Лицензии на дизайн оплачиваются отдельно — вы платите только за то, что выбираете для производства.",
    },
    socialProof: [
      "48+ предварительно проработанных дизайнов",
      "Дизайнеры из 6 стран",
      "AI-наборы генерируются менее чем за 3 минуты",
      "Прибыльно с первой единицы",
    ],
    billing: {
      monthly: "Ежемесячно",
      annual: "Ежегодно",
      savingActive: "Экономия ~17%",
      savingPrompt: "Сэкономьте ~17% при годовой оплате",
    },
    tiers: {
      explorer: {
        name: "Discover",
        tagline: "Посмотрите, что возможно",
        description: "Просматривайте каталог, сохраняйте избранное и получите 200 приветственных кредитов для создания первого AI-маркетингового набора — бесплатно, без карты.",
        features: [
          "Полный каталог — каждый дизайнер, каждое изделие",
          "Сохраняйте дизайны в список избранного",
          "200 приветственных Studio Credits (однократно)",
          "Сгенерируйте 2 Essentials Kits, чтобы оценить качество",
          "Выберите количество — мы управляем остальным, от CAD до доставки",
        ],
        cta: "Начать бесплатно",
      },
      professional: {
        name: "Source",
        tagline: "Прямой доступ к дизайнерам",
        description: "Полный доступ к sourcing'у. Прямые отношения с дизайнерами. 1 500 кредитов/месяц — достаточно для 15 полных маркетинговых наборов или 150 каталожных снимков.",
        mostPopular: "Самый популярный",
        features: [
          "Всё из Discover, плюс:",
          "1 500 Studio Credits каждый месяц",
          "Прямой sourcing от дизайнеров — без посредников",
          "Переговоры об условиях лицензирования через личные сообщения",
          "AI-маркетинговый набор: lifestyle-фото, лукбук, социальные материалы — готово за минуты",
          "Стандартный и эксклюзивный уровни лицензирования",
          "Фильтрация по металлу, камню, стилю, цене и доступности",
        ],
        cta: "Начать Source",
      },
      atelier: {
        name: "Atelier",
        tagline: "Ваша частная дизайн-мастерская",
        description: "Полная передача интеллектуальной собственности через лицензирование Vault. Ваш персональный менеджер по производству управляет всем — от CAD до доставки. 5 000 кредитов/месяц.",
        features: [
          "Всё из Source, плюс:",
          "Лицензирование Vault — полная передача IP и CAD-файлов",
          "5 000 Studio Credits каждый месяц",
          "Персональный менеджер по производству — ваш единый контакт",
          "Приоритетная поддержка — прямая линия, ответ в тот же день",
          "Ранний доступ к новым дизайнерам до публичного размещения",
          "Индивидуальные sourcing-брифы — скажите, что вам нужно, мы найдём",
          "Условия лицензирования для заказов с несколькими артикулами",
          "Cara AI — создавайте оригинальные украшения с помощью AI",
        ],
        cta: "Начать Atelier",
      },
      enterprise: {
        name: "Enterprise",
        tagline: "Масштабируйтесь без ограничений",
        description: "Для розничных сетей и крупных дистрибьюторов. Индивидуальный объём кредитов, API-интеграции, премиальное управление производством и выделенная команда аккаунта.",
        customLabel: "Индивидуально",
        customSublabel: "Настроено под ваш объём и требования",
        features: [
          "Всё из Atelier, плюс:",
          "Индивидуальный объём Studio Credits — соответствующий скорости вашего каталога",
          "Доступ к API — каталог, наборы и управление заказами",
          "Премиальное управление производством на нескольких фабриках",
          "Выделенная команда аккаунта с ежеквартальными бизнес-обзорами",
          "Индивидуальное SLA, онбординг и обучение",
          "Многопользовательский командный доступ с ролевыми разрешениями",
          "Согласованные условия лицензирования и оптовые скидки",
          "Приоритетная очередь производства — ваши заказы отгружаются первыми",
        ],
        cta: "Связаться с нашей командой",
      },
    },
    priceDisplay: {
      free: "Бесплатно",
      freeSublabel: "Без банковской карты",
      perMonth: "/мес",
      saveBadge: "(экономия ~17%)",
      orAnnualOption: "или {amount}/год",
      billedAnnually: "Оплата {amount} ежегодно",
      licensesSeparate: "Лицензии на дизайн оплачиваются отдельно",
    },
    getStarted: "Начать",
    studioCredits: {
      overline: "Studio Credits",
      heading: "Фиксированная стоимость за действие. Без сюрпризов.",
      body: "Каждое AI-действие — от каталожной фотографии до консультации по дизайну с Cara — стоит фиксированное количество кредитов из вашего ежемесячного лимита. Нужно больше? Пополняющие пакеты доступны мгновенно и никогда не истекают.",
      link: "Посмотреть стоимость кредитов и пополняющие пакеты →",
    },
    roi: {
      overline: "Цифры",
      heading: "Во что реально обходится запуск нового артикула.",
      body: "Традиционный sourcing скрывает маржу в скрытых расходах и месяцах ожидания. Caratzon сокращает весь процесс.",
      traditionalLabel: "Традиционный sourcing",
      caratzonLabel: "Caratzon",
      rows: [
        { metric: "Время выхода на рынок", traditional: "4–6 месяцев", caratzon: "3 недели" },
        { metric: "R&D и CAD-моделирование", traditional: "$800 – $2 500+", caratzon: "$0 — предварительно проработано" },
        { metric: "Физические образцы", traditional: "$500+ за раунд", caratzon: "$0 — AI-рендеры превью" },
        { metric: "Маркетинговая фотография", traditional: "$2 000+ за кампанию", caratzon: "~$7 через Studio Credits" },
        { metric: "Точка безубыточности", traditional: "~50 проданных единиц", caratzon: "Единица 1" },
      ],
      note: "Предварительно проработанные CAD-файлы. Цифровые двойники для образцов. AI-фотография. Каждый дизайн на Caratzon прибылен с первой единицы.",
    },
    faq: {
      overline: "Частые вопросы",
      heading: "Всё, что вам нужно знать.",
      items: [
        {
          q: "Что входит в плату за платформу, а что в лицензии на дизайн?",
          a: "Ваше членство предоставляет доступ к каталогу, AI-маркетинговым инструментам (Studio Credits) и возможностям прямого sourcing'а. Лицензии на дизайн оплачиваются отдельно — вы платите только за те дизайны, которые выбираете для производства. Считайте членство своим инструментарием, а лицензии — материалами.",
        },
        {
          q: "Что происходит с неиспользованными Studio Credits?",
          a: "Ежемесячные кредиты сгорают в конце каждого расчётного периода. Это обеспечивает справедливое ценообразование для всех. Если вам регулярно нужно больше, перейдите на более высокий тариф или приобретите пополняющие пакеты — пополняющие кредиты никогда не истекают.",
        },
        {
          q: "Можно ли сменить тариф или отменить подписку в любое время?",
          a: "Да. Переход на более высокий тариф — мгновенно, переход на более низкий — в конце расчётного периода. Без долгосрочных контрактов, без комиссии за отмену. Годовые тарифы можно отменить с пропорциональным возвратом средств за неиспользованные месяцы.",
        },
        {
          q: "Что фактически включает лицензия на дизайн?",
          a: "Каждая лицензия включает предварительно проработанный CAD-файл (направляется прямо в производство — вы никогда не работаете с файлом напрямую), полные коммерческие права выбранного уровня (Стандарт, Эксклюзив или Vault) и Essentials Kit (каталожные снимки, спецификации, SEO-копия). Caratzon Kits с lifestyle-фотографией генерируются с помощью ваших Studio Credits.",
        },
        {
          q: "Как работает лицензирование Vault (передача IP)?",
          a: "Доступно на тарифах Atelier и Enterprise. Vault предоставляет вам постоянное эксклюзивное право собственности на дизайн — включая оригинальные CAD-файлы. Дизайнер получает единовременный выкуп. После передачи в Vault дизайн удаляется из публичного каталога. Он ваш.",
        },
        {
          q: "Нужно ли мне самостоятельно управлять производством?",
          a: "Нет. Каждый производственный заказ проходит через нашу проверенную производственную сеть EPH. Литьё, закрепка, контроль качества и логистика управляются от начала до конца. Вы утверждаете этапы из своей панели управления — мы управляем остальным.",
        },
      ],
    },
    bottomCta: {
      heading: "Готовы к другому подходу к sourcing'у?",
      body: "Начните бесплатно с Discover. Переходите на более высокий тариф, когда будете готовы — без обязательств.",
      button: "Просмотреть каталог",
      link: "Нужен индивидуальный тариф? Давайте поговорим.",
    },
  },

  // ─── AI page (/ai) ────────────────────────────────────────────────────────
  ai: {
    meta: {
      title: "Cara AI — Инженерный интеллект для украшений · Caratzon",
      description: "Cara — это AI для инженерии украшений, которая проектирует для производства. Компенсация усадки, структурный анализ, перечень материалов и готовые к производству концепции — не фантастические рендеры.",
      socialDescription: "AI, которая разрабатывает украшения для реального производства. Усадка, допуски, механика литья, носкость — каждая концепция готова к производству.",
    },
    hero: {
      headingLine1: "Не инструмент генерации изображений.",
      headingLine2: "AI для инженерии украшений.",
      ctaDemo: "Попробовать Cara AI — Живая демонстрация",
      ctaCredits: "Посмотреть стоимость кредитов",
    },
    pillars: {
      label: "Инженерные ограничения",
      headingLine1: "Каждая концепция проходит стресс-тест",
      headingLine2: "до того, как покинет экран.",
    },
    capabilities: {
      label: "Возможности",
      headingLine1: "От эскиза на салфетке",
      headingLine2: "до готового к производству чертежа.",
    },
    whatYouCanAsk: {
      label: "Что вы можете спросить",
      heading: "Диалог, а не поле для запроса.",
      groups: [
        {
          title: "Консультация по дизайну",
          prompts: [
            "«Проанализируй этот эскиз на пригодность к производству»",
            "«Какова минимальная толщина стенки для 18K белого золота?»",
            "«Выдержит ли эта halo-закрепка ежедневный износ?»",
          ],
        },
        {
          title: "Материаловедение",
          prompts: [
            "«Сравни платину 950 и 18K для этого профиля шинки»",
            "«Какой сплав использовать для крапана 0,8 мм?»",
            "«Коэффициент усадки при литьё по выплавляемым моделям в 14K жёлтом?»",
          ],
        },
        {
          title: "Визуальная генерация",
          prompts: [
            "«Сделай рендер этой концепции в розовом золоте с паве-шинкой»",
            "«Сгенерируй мудборд для помолвочных колец в стиле ар-деко»",
            "«Создай видео-поворот 360° этого дизайна»",
          ],
        },
        {
          title: "Планирование производства",
          prompts: [
            "«Сгенерируй перечень материалов для этого кольца»",
            "«Оцени стоимость производства в 18K с бриллиантами VS1»",
            "«Каков производственный срок для партии 50 единиц?»",
          ],
        },
      ],
    },
    creditsTeaser: {
      label: "Studio Credits",
      heading: "Прозрачный AI с фиксированной стоимостью.",
      body: "Каждое действие Cara имеет фиксированную стоимость в кредитах — 5 кредитов за сообщение, 15 за рендер изображения, 25 за видео 360°. Без поштучного тарифа, без сюрпризов.",
      link: "Посмотреть все стоимости кредитов и пополняющие пакеты →",
    },
    finalCta: {
      heading: "Убедитесь сами.",
      body: "Попробуйте живую демонстрацию — без аккаунта. Загрузите эскиз, задайте вопрос и наблюдайте, как Cara разрабатывает готовую к производству концепцию.",
      ctaDemo: "Попробовать Cara AI — Живая демонстрация",
      ctaPricing: "Посмотреть тарифы",
    },
  },

  // ─── Designer AI page (/designers/ai) ──────────────────────────────────────
  designerAi: {
    meta: {
      title: "Cara AI для дизайнеров — Caratzon",
      description: "Проверяйте ваш CAD на пригодность к производству, генерируйте портфолио-рендеры, видео 360° и лукбук-фотографии — всё на базе Cara AI. Сосредоточьтесь на дизайне; пусть Cara занимается инженерией.",
      socialDescription: "AI-помощник для дизайнеров украшений. Проверяйте CAD, генерируйте рендеры, создавайте портфолио — готово к производству с первого дня.",
    },
    hero: {
      overline: "Cara AI · Для дизайнеров",
      headingLine1: "Проектируйте без страха.",
      headingLine2: "Cara занимается инженерией.",
      body: "Cara — ваш AI-помощник: мастер-ювелир и инженер-механик в одном лице. Проверяйте CAD на пригодность к производству, генерируйте рендеры студийного качества и создавайте портфолио, которое побуждает покупателей обращаться к вам. Вы сосредоточены на творческой работе.",
      ctaDemo: "Попробовать Cara AI — Живая демонстрация",
      ctaStudio: "Открыть Studio",
    },
    pillars: {
      label: "Что Cara делает для вас",
      headingLine1: "От вашего CAD-файла",
      headingLine2: "до готового к производству портфолио.",
      items: [
        {
          stat: "CAD → Рендер",
          label: "Создание портфолио",
          body: "Загрузите ваш .3DM или .STL, и Cara сгенерирует студийные предметные снимки, lifestyle-рендеры и видео-поворот 360° — такой уровень визуалов раньше стоил тысячи за изделие.",
        },
        {
          stat: "±0,15 мм",
          label: "Проверка пригодности к производству",
          body: "Cara анализирует ваш дизайн по реальным производственным допускам — толщина стенки, компенсация усадки, прочность крапанов, радиусы кромок — и выявляет проблемы до того, как покупатель их увидит.",
        },
        {
          stat: "Auto BOM",
          label: "Перечень материалов",
          body: "Получите мгновенный точный перечень материалов — вес металла, количество камней, стоимость по сплавам — чтобы уверенно устанавливать цены, а покупатели видели прозрачные оценки производства.",
        },
        {
          stat: "Ваша история",
          label: "AI-сторителлинг",
          body: "Cara пишет редакционный нарратив для каждого изделия и коллекции. Не шаблонный текст — история, основанная на вашей философии дизайна, материалах и творческом замысле.",
        },
      ],
    },
    capabilities: {
      label: "Возможности для дизайнеров",
      headingLine1: "Всё необходимое,",
      headingLine2: "чтобы перейти от файла к публикации.",
      items: [
        { label: "Валидация CAD", detail: "Структурный анализ по производственным допускам EPH — толщина стенки, поднутрения, углы уклона" },
        { label: "Рендеры изделий", detail: "Предметные снимки студийного качества в нескольких ракурсах, металлах и конфигурациях камней" },
        { label: "Видео-поворот 360°", detail: "Анимированные превью-повороты, которые покупатели могут встраивать в презентации и питч-деки" },
        { label: "Лукбук-фотография", detail: "AI-generated lifestyle и фотографии на модели, стилизованные под эстетику вашего бренда" },
        { label: "Нарративы коллекций", detail: "Сгруппируйте дизайны в коллекции, и Cara напишет единую редакционную историю для всех изделий" },
        { label: "Материальная экспертиза", detail: "Рекомендации по сплавам для золота 10K–24K, серебра 925, платины 950 — усадка, твёрдость, финиш" },
      ],
    },
    workflow: {
      label: "Ваш рабочий процесс с Cara",
      heading: "Загрузите. Проверьте. Опубликуйте.",
      steps: [
        {
          title: "Загрузите ваш CAD",
          body: "Перетащите файл .3DM, .STL или .STEP в Studio. Cara считывает геометрию и сразу приступает к анализу.",
        },
        {
          title: "Просмотрите и уточните",
          body: "Cara выявляет проблемы с пригодностью к производству, предлагает исправления и генерирует полный перечень материалов. Общайтесь с Cara для итераций — спрашивайте о сплавах, допусках или альтернативных закрепках.",
        },
        {
          title: "Создайте ваше портфолио",
          body: "Один клик генерирует предметные рендеры, lifestyle-снимки, видео 360° и редакционную историю. Ваш дизайн превращается из необработанного файла в витрину для покупателей.",
        },
        {
          title: "Опубликуйте в Inspire и каталог",
          body: "Разместите ваш проверенный, отрендеренный дизайн на странице создателя Inspire и в торговом каталоге в один шаг. Покупатели найдут вашу работу с визуалами и нарративом — готовую к лицензированию.",
        },
      ],
    },
    closing: "Cara не заменяет вашу творческую силу — она убирает всё, что стоит между вашим дизайном и покупателем, которому нужно его увидеть. Никакого фотографа. Никакого копирайтера. Никакого заводского куратора. Только ваше видение, подтверждённое производством и красиво представленное.",
    creditsTeaser: {
      label: "Studio Credits",
      heading: "AI с фиксированной стоимостью. Без сюрпризов.",
      body: "Каждое действие Cara имеет прозрачную стоимость в кредитах — 5 кредитов за сообщение, 15 за рендер, 25 за видео 360°. Кредиты включены в ваш тариф, пополнение доступно в любое время.",
      link: "Посмотреть стоимость кредитов →",
    },
    finalCta: {
      heading: "Ваши дизайны заслуживают быть увиденными.",
      body: "Загрузите первый CAD, позвольте Cara проверить и отрендерить его, опубликуйте в Inspire — всё менее чем за пять минут.",
      ctaDemo: "Попробовать Cara AI — Живая демонстрация",
      ctaStudio: "Открыть Studio",
    },
  },

  // ─── AI Credits page (/ai/credits) ────────────────────────────────────────
  aiCredits: {
    meta: {
      title: "Studio Credits — Caratzon",
      description: "Узнайте, как работают Studio Credits на Caratzon — AI-действия с фиксированной стоимостью для фото-наборов, консультаций по дизайну и многого другого. Без поштучной оплаты.",
      socialDescription: "AI-действия с фиксированной стоимостью для фото-наборов, консультаций по дизайну и многого другого.",
    },
    hero: {
      label: "Studio Credits",
      heading: "Прозрачный AI с фиксированной стоимостью.",
      body: "Каждое AI-действие на Caratzon стоит фиксированное количество Studio Credits. Никакой поштучной оплаты, никакого поштучного тарифа, никаких сюрпризов.",
    },
    howItWorks: {
      label: "Как это работает",
      heading: "Получайте, тратьте, пополняйте.",
      steps: [
        { step: "01", title: "Получайте кредиты", body: "Платные тарифы включают ежемесячный лимит кредитов. Аккаунты Discover получают 200 приветственных кредитов для знакомства." },
        { step: "02", title: "Тратьте на любые действия", body: "Фото-наборы, лукбуки, консультации с Cara AI — каждое действие имеет фиксированную стоимость, отображаемую заранее." },
        { step: "03", title: "Пополняйте при необходимости", body: "Покупайте кредитные пакеты мгновенно. Пополняющие кредиты никогда не истекают — кредиты подписки сбрасываются каждый расчётный период." },
      ],
    },
    photoStudio: {
      label: "Photo Studio",
      heading: "Материалы, готовые к производству.",
      body: "Загрузите дизайн и получите готовую к каталогу фотографию, листы линии и маркетинговый текст — всё создано AI за минуты.",
    },
    caraAi: {
      label: "Cara AI",
      heading: "Ваш партнёр по инженерии дизайна.",
      body: "Cara — AI-инженер украшений, которая понимает металлургию, закрепку камней, структурную целостность и производственные ограничения. Спрашивайте о чём угодно — от материаловедения до оценок BOM.",
      note: "Текстовое сообщение (5 кредитов) списывается при отправке. Инструменты для изображений и видео списываются при запуске AI — вы увидите стоимость до выполнения. Типичная сессия из 10 сообщений с несколькими рендерами расходует около 100–150 кредитов.",
    },
    monthlyAllowances: {
      label: "Ежемесячные лимиты",
      heading: "Кредиты, включённые в ваш тариф.",
      tableHeaders: ["Тариф", "Ежемесячные кредиты", "Приветственные кредиты", "Цена"],
      rows: [
        { plan: "Discover", monthly: "—", welcome: "200", price: "Бесплатно" },
        { plan: "Source", monthly: "1 500", welcome: "—", price: "$149/мес" },
        { plan: "Atelier", monthly: "5 000", welcome: "—", price: "$399/мес" },
        { plan: "Enterprise", monthly: "Индивидуально", welcome: "—", price: "Индивидуально" },
      ],
      note: "Кредиты подписки сбрасываются каждый расчётный период — неиспользованные кредиты не переносятся. Пополняющие кредиты никогда не истекают.",
    },
    topUpPacks: {
      label: "Пополняющие пакеты",
      heading: "Купите дополнительно, храните бессрочно.",
      body: "Запускаете большую коллекцию? Купите кредиты мгновенно — они никогда не истекают.",
      bestValue: "Лучшее предложение",
      creditsLabel: "кредиты",
      perCredit: "¢ за кредит",
      buyPrefix: "Купить",
      buySuffix: "кредитов",
      note: "Подписка всегда выгоднее — кредиты подписки дешевле за единицу, чем пополняющие пакеты.",
      compareLink: "Сравнить тарифы",
    },
    faq: {
      label: "Частые вопросы",
      heading: "Кредиты, объяснённые.",
      items: [
        { q: "Что происходит, если кредиты заканчиваются в середине разговора с Cara?", a: "Проверка текстовых сообщений выполняется перед отправкой — вы увидите предложение пополнить. Инструменты для изображений и видео проверяют баланс перед генерацией, поэтому AI сообщит вам о нехватке кредитов до запуска дорогостоящего действия." },
        { q: "Переносятся ли неиспользованные кредиты подписки?", a: "Нет. Кредиты подписки сбрасываются в начале каждого расчётного периода. Если вам регулярно нужно больше, рассмотрите переход на более высокий тариф или покупку пополняющего пакета — пополняющие кредиты никогда не истекают." },
        { q: "Может ли одно сообщение Cara стоить больше 5 кредитов?", a: "Да. Базовое сообщение стоит 5 кредитов, но если Cara генерирует изображения (по 15 каждое) или видео (по 25 каждое) в ответе, они списываются дополнительно. Типичная консультация по дизайну с 2–3 рендерами занимает около 50–70 кредитов." },
        { q: "Списываются ли кредиты с администраторов?", a: "Нет. Аккаунты администраторов освобождены от списания кредитов для внутреннего тестирования и операций." },
        { q: "В чём разница между Essentials Kit и Caratzon Kit?", a: "Essentials Kit (10 кредитов) генерирует готовые к каталогу снимки, спецификации и SEO-копию. Caratzon Kit (100 кредитов) включает всё из Essentials плюс полный лукбук с lifestyle-фотографией, редакционными изображениями и материалами для социальных сетей." },
      ],
    },
    actions: {
      photoStudio: [
        { action: "Essentials Kit", detail: "Каталожные снимки, спецификации и SEO-копия" },
        { action: "Caratzon Kit", detail: "Полная фотостудия — lifestyle-фотография, листы линии и материалы для социальных сетей" },
        { action: "Повторная генерация одного фото", detail: "Повторно сгенерируйте одно фото, которое вам не понравилось" },
        { action: "PDF лукбука", detail: "Брендированный оптовый лист линии" },
      ],
      caraAi: [
        { action: "Сообщение", detail: "Анализ эскиза, вопросы и ответы, материаловедение, структурная проверка или консультация по дизайну" },
        { action: "Эскиз", detail: "Быстрый концептуальный эскиз по текстовому описанию" },
        { action: "Рендер изображения", detail: "Точный рендер эскиза в концепцию, мудборд, каталожный снимок или корректировка дизайна" },
        { action: "Лукбук-изображение", detail: "Редакционное лукбук-изображение для презентационных деков" },
        { action: "Видео 360°", detail: "Видео-поворот вашего дизайна со всех ракурсов" },
      ],
    },
  },

  demo: {
    meta: {
      title: "Демонстрация Cara AI · Caratzon",
      description: "Попробуйте Cara — AI для инженерии украшений, которая проектирует для производственной линии и для руки человека. Усадка, допуски, механика литья, носкость — каждая концепция готова к производству. Живая демонстрация.",
    },
    splash: {
      eyebrow: "AI для инженерии украшений",
      heading: "Познакомьтесь с Cara",
      subheading: "AI для инженерии, которая создаёт украшения, которые фабрика может изготовить, а клиент захочет носить — с самого первого эскиза.",
      cta: "Начать проектирование",
      enterHint: "Нажмите {key} чтобы начать",
      promptDivider: "или попробуйте запрос",
      signIn: "Войти",
      noAccount: "Аккаунт не требуется",
      notSaved: "Разговоры не сохраняются",
      fullAccess: "Полный доступ к платформе →",
      namePlaceholder: "Ваше имя (отображается в галерее)",
      nameHint: "Чтобы мы могли указать вас как автора",
    },
    prompts: [
      { label: "Помолвочное кольцо", text: "Создай современное помолвочное кольцо-солитер с овальным бриллиантом 1 карат в 18k розовом золоте" },
      { label: "Коктейльное кольцо ар-деко", text: "Создай смелое коктейльное кольцо в стиле ар-деко с изумрудами и ониксом" },
      { label: "Теннисный браслет", text: "Создай минималистичный теннисный браслет с чередующимися сапфирами и бриллиантами из платины" },
      { label: "Скульптурный манжет", text: "Хочу скульптурный золотой манжет с органическими плавными линиями, без камней, только чистая работа с металлом" },
    ],
    capabilities: [
      { label: "От эскиза до производственного чертежа", desc: "Загрузите эскиз — Cara разрабатывает его для производственной линии" },
      { label: "Анализ литья и усадки", desc: "Компенсация усадки для конкретных металлов, проверка толщины стенки" },
      { label: "Картирование допусков камней", desc: "Размещение крапанов, глубина посадки и зазор рундиста — проработано" },
      { label: "Носкость и комфорт", desc: "Профиль шинки, баланс веса, радиусы кромок — разработано для ношения целый день" },
    ],
    header: {
      brandLabel: "Cara AI",
      tabs: { create: "Создать", gallery: "Галерея" },
      back: "← Назад",
      fullAccess: "Получить полный доступ",
    },
    gallery: {
      loading: "Загрузка галереи…",
      emptyTitle: "Дизайнов пока нет",
      emptyBody: "Будьте первым, кто что-то создаст. Дизайны, созданные в этой демо-сессии, появятся здесь для всеобщего просмотра.",
      emptyAction: "Создать первый дизайн",
      sectionTitle: "Создано на этом мероприятии",
      countSingular: "{count} дизайн создан посетителями демонстрации",
      countPlural: "{count} дизайнов создано посетителями демонстрации",
      createYours: "Создайте свой",
      createdWith: "Создано с Cara AI",
      generating: "Генерация истории…",
      viewStory: "Посмотреть историю",
      toolLabels: {
        render: "AI Рендер",
        mood_board: "Мудборд",
        catalog_render: "Каталожный рендер",
      },
      fallbackTool: "Рендер",
      timeAgo: {
        justNow: "Только что",
        minutes: "{n} мин. назад",
        hours: "{n} ч. назад",
        days: "{n} дн. назад",
      },
    },
    story: {
      backToGallery: "← Назад в галерею",
      fromSketch: "Из эскиза",
      theSketch: "Эскиз",
      sketchCaption: "Оригинальный эскиз от {name}",
      theDetails: "Детали",
      detailType: "Тип",
      detailMetal: "Металл",
      detailStyle: "Стиль",
      detailStones: "Камни",
      detailFinish: "Финиш",
      detailWeight: "Вес",
      detailPrice: "Оцен. цена",
      structuralAnalysis: "Структурный анализ",
      theStory: "История",
      showcase: "Витрина",
      designJourney: "Путь дизайна",
      theConversation: "Разговор",
      moreMessages: "Ещё {n} сообщений в этом разговоре",
      createdBy: "Создано",
      createdWith: "Создано с Cara AI на {event}",
      eventName: "Живая демонстрация",
      tryIt: "Попробуйте Cara сами →",
      artifactLabels: {
        render: "Рендер дизайна",
        catalog_render: "Каталожный рендер",
        concept_sketch: "Концептуальный эскиз",
        mood_board: "Мудборд",
        structural_analysis: "Структурный анализ",
        material_recommendation: "Анализ материалов",
        bom: "Перечень материалов",
        sketch_analysis: "Анализ эскиза",
        lookbook: "Лукбук",
        video: "Видео-поворот",
        inspiration: "Анализ вдохновения",
        design_specs: "Спецификации дизайна",
        finalize_design: "Финализированный дизайн",
        gallery_share: "Поделиться в галерее",
      },
    },
    brands: {
      splash: {
        eyebrow: "Дизайн-интеллект для брендов",
        subheading: "Опишите ваше видение. Увидьте рендер. Отправьте в производство.",
        cta: "Начать исследование",
        namePlaceholder: "Ваше имя или бренд",
        nameHint: "Чтобы мы могли персонализировать ваш опыт",
        fullAccess: "Открыть торговый аккаунт →",
      },
      prompts: [
        { label: "Индивидуальный теннисный браслет", text: "Настрой этот теннисный браслет в розовом золоте для моей весенней коллекции" },
        { label: "Фирменный кулон", text: "Создай фирменный кулон с геометрическим мотивом нашего бренда — минималистичный, 14k золото" },
        { label: "Варианты обручального кольца", text: "Покажи варианты минималистичного набора обручальных колец по трём ценовым категориям" },
        { label: "От эскиза к производству", text: "У меня есть эскиз манжета — можете сделать его готовым к производству?" },
      ],
      capabilities: [
        { label: "Превью кастомизации", desc: "Посмотрите ваш дизайн в фотореалистичных деталях" },
        { label: "Варианты материалов и финишей", desc: "Исследуйте металлы, камни и финиши — рядом друг с другом" },
        { label: "Примерная стоимость", desc: "Мгновенные оценки для прототипирования и мелкосерийного производства" },
        { label: "Производственный срок", desc: "От концепции до доставки — полный производственный путь" },
      ],
      header: {
        fullAccess: "Открыть торговый аккаунт",
      },
    },
  },

  // ─── Production Dashboard ─────────────────────────────────────────────────
  productionDashboard: {
    title: "Панель управления производством",
    tabs: {
      portfolio: "Портфолио",
      board: "Доска",
    },
    zoom: "Масштаб",
    day: "День",
    week: "Неделя",
    month: "Месяц",
    search: "Поиск...",
    searchPlaceholder: "Поиск планов, дизайнов, производителей...",
    keyboardShortcuts: "Горячие клавиши",
    groupBy: "Группировать по",
    status: "Статус",
    phase: "Фаза",
    dragDisabledInPhase: "Перетаскивание отключено в режиме фазы",
    backToPortfolio: "Портфолио",
    subOrders: "подзаказы",
    unassigned: "Не назначено",
    noItems: "Нет элементов",
    densityCompact: "Компактный вид",
    densityComfortable: "Удобный вид",

    // Empty states
    empty: {
      portfolioTitle: "Нет активных производств",
      portfolioDescription: "Планы, созданные в планировщике производства, появятся здесь после утверждения.",
      portfolioLink: "Открыть планировщик производства",
      boardTitle: "Нет подзаказов",
      boardDescription: "Ни один подзаказ не соответствует вашим фильтрам.",
      boardLink: "Просмотреть портфолио",
      planTitle: "Нет подзаказов",
      planDescription: "У этого плана пока нет подзаказов.",
      planLink: "Открыть планировщик производства",
    },

    // Filter dropdowns
    filters: {
      allStatuses: "Все статусы",
      allDesigners: "Все дизайнеры",
      allBuyers: "Все покупатели",
    },

    // Command palette
    command: {
      noResults: "Результаты не найдены.",
      productionPlans: "Производственные планы",
      quickActions: "Быстрые действия",
      goToPortfolio: "Перейти к портфолио",
      goToBoard: "Перейти к доске",
      navigate: "Навигация",
      open: "Открыть",
      close: "Закрыть",
    },

    // Kanban
    kanban: {
      blockedAndCancelled: "Заблокировано и отменено",
      columns: {
        PENDING: "Ожидает",
        QUOTED: "Оценено",
        ASSIGNED: "Назначено",
        IN_PROGRESS: "В работе",
        QA: "Контроль качества",
        COMPLETED: "Завершено",
        BLOCKED: "Заблокировано",
        CANCELLED: "Отменено",
      },
    },

    // Gantt
    gantt: {
      today: "Сегодня",
      criticalPath: "Критический путь",
      duration: "Длительность",
      days: "дней",
      manufacturer: "Производитель",
      dependsOn: "Зависит от",
    },

    // Detail panel
    detail: {
      status: "Статус",
      manufacturer: "Производитель",
      duration: "Длительность",
      dependencies: "Зависимости",
    },

    // Hover card
    hover: {
      duration: "Длительность",
      manufacturer: "Производитель",
    },
  },

  // ─── Buyer Milestone Tracker ──────────────────────────────────────────────
  buyerTracker: {
    title: "Прогресс производства",
    inProgress: "В работе",
    pending: "Ожидает",
    milestones: {
      orderConfirmed: "Заказ подтверждён",
      designEngineering: "Инженерия дизайна",
      sampleProduction: "Производство образца",
      approval: "Утверждение",
      manufacturing: "Производство",
      finalFinishing: "Финальная отделка",
      qualityCheckDelivery: "Контроль качества и доставка",
    },
  },
};
